import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAuth, useGeneration } from "@/app";
import { createNewSong, createSongFromCustomText, editSongDirect, editSongWithAI, generateMusic, getSongById, getTextById, searchGenres, searchInstruments, searchMoods, searchReasons, searchRecipients, searchTexts, waitForSongGeneration, } from "@/shared/api/songs";
import { cn } from "@/shared/lib/utils";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
import { ymReachGoal, ymReachGoalOnce } from "@/shared/lib/yandexMetrika";
import { isGuestLlmLimitError, resolveTextActionErrorMessage, } from "@/shared/lib/guestLimitError";
import { setPendingMusicGeneration } from "@/shared/lib/pendingMusicGeneration";
import { SONG_CREATION_STATE_STORAGE_KEY, clearSongCreationPersistedState, } from "@/shared/lib/songCreationPersistedState";
import { clearPendingSongGeneration, savePendingSongGeneration, } from "@/shared/lib/pendingSongGeneration";
import { isInsufficientBalanceError } from "@/shared/lib/insufficientBalanceError";
import { isTextNotFoundError } from "@/shared/lib/staleTextError";
import { markHasCreatedSongs } from "@/shared/lib/createdSongsFlag";
import { isTelegramMiniApp } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
import { listVoiceProfiles, isVoiceProfileReady, } from "@/shared/api/voice";
import { useAutoReadVoiceReady } from "@/features/notification-center";
import { VoiceStudioModal } from "@/features/voice";
import { notifications } from "@/shared/ui";
import { IconChevronLeft, IconRefresh, IconSparkles, IconWand, } from "@tabler/icons-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Accordion, Button as VpButton, IconChip, Loader as VpLoader, Modal as VpModal, SegmentedControl as VpSegmentedControl, Select as VpSelect, TextField as VpTextField, Textarea as VpTextarea, } from "@/shared/ui";
import { GenerateHeader } from "./parts/GenerateHeader";
import { StyleReferenceField, } from "./parts/StyleReferenceField";
import { isStyleReferenceNotFoundError } from "@/shared/lib/styleReference";
import styles from "./GenerateSongPage.module.scss";
/**
 * Бэкенд отдаёт title с ведущими emoji (например, "🎂 День рождения").
 * В новом дизайне ноль emoji — снимаем префикс на стороне отображения, бэкенд
 * не трогаем. Если строка целиком из emoji, возвращаем оригинал (fallback).
 */
const stripLeadEmoji = (s) => {
    const out = s
        .replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}️‍\u{1F1E6}-\u{1F1FF}]+\s*/u, "")
        .trim();
    return out || s;
};
/**
 * Подпись для чипа-тега (recipient / reason / genre). Эмодзи оставляем
 * как есть — показываем title целиком. Возвращаем `null`, только если
 * значения нет или это плейсхолдер «—»: блок «Кому песня» убран из UI и
 * в запрос уходит recipient="—", из-за чего на карточке сохранённого
 * текста рисовался чип с бессмысленным прочерком.
 */
const tagLabel = (value) => {
    if (!value)
        return null;
    const raw = (typeof value === "string" ? value : value.title ?? "").trim();
    if (!raw || raw === "—" || raw === "-")
        return null;
    return raw;
};
/**
 * Приводит список настроений/инструментов из ответа песни к массиву названий.
 * Бэк обычно отдаёт string[] (как их принимает generate-music), но на всякий
 * случай поддерживаем и объекты вида `{ title }`.
 */
const toTitleList = (value) => {
    if (!Array.isArray(value))
        return [];
    return value
        .map((item) => (typeof item === "string" ? item : item?.title ?? "").trim())
        .filter((s) => s.length > 0);
};
const MELODY_KEY_OPTIONS = [
    "C", "C#", "Cb", "D", "D#", "Db", "E", "E#", "Eb",
    "F", "F#", "Fb", "G", "G#", "Gb", "A", "A#", "Ab",
    "B", "B#", "Bb",
].flatMap((root) => [
    { value: `${root} Major`, label: `${root} Major` },
    { value: `${root} Minor`, label: `${root} Minor` },
]);
import { useMediaQuery } from "@/shared/lib";
function scrollGenerateSongPageToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Реальные скролл-контейнеры: обёртка миниаппа (мобилка) и .contentBody
    // (web-раскладка). Без этого при переходе текст → музыка скролл не
    // сбрасывался на мобильном.
    document
        .querySelectorAll('[data-scroll-root], [class*="contentBody"]')
        .forEach((el) => {
        el.scrollTop = 0;
    });
}
export const GenerateSongPage = ({ language, darkMode, openRegisterModal, }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isGuest, user, refreshUser, schedulePostEmailAuthAction, } = useAuth();
    // Доступ к ручкам текстов (/texts/*) есть и у гостя: авторизованный аккаунт
    // ИЛИ активная гостевая сессия. Используем для таба «Мои песни» и загрузки
    // сохранённых текстов.
    const canUseTexts = isAuthenticated || isGuest;
    // Воронка, шаг 2: открыт экран генерации текста (зонтичное, один раз на маунт).
    // Плюс разбивка по режиму: на открытии активен таб «ИИ напишет» → text_ai_view.
    useEffect(() => {
        trackEvent(AnalyticsEvent.TEXT_GEN_SCREEN_OPEN, {
            entry_point: "main_menu",
        });
        trackEvent(AnalyticsEvent.TEXT_AI_VIEW);
        ymReachGoalOnce("text_gen_screen_open", { mode: "classic" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Язык каталога (genres/reasons/...). Для авторизованных бэк отдаёт
    // каталог по `user.language` (тело запроса игнорируется), поэтому
    // зависим ТОЛЬКО от него: он меняется лишь после того, как PATCH
    // профиля прошёл и профиль перечитан (см. useSelectLanguage). Завязка
    // на язык интерфейса здесь вернула бы гонку — каталог отставал бы на
    // одно переключение. Для неавторизованных язык задаётся телом запроса,
    // поэтому ориентируемся на язык интерфейса.
    const catalogLanguage = isAuthenticated
        ? user?.language === "en"
            ? "en"
            : "ru"
        : language;
    const { setIsGeneratingText: setGlobalIsGeneratingText } = useGeneration();
    const [activeTab, setActiveTab] = useState("text");
    // Глобальные табы для выбора типа текста (новый/сохраненный)
    const [textSourceType, setTextSourceType] = useState("new");
    const [savedTexts, setSavedTexts] = useState([]);
    const [isLoadingSavedTexts, setIsLoadingSavedTexts] = useState(false);
    const [selectedSavedText, setSelectedSavedText] = useState(null);
    const [savedTextEditPrompt, setSavedTextEditPrompt] = useState("");
    const [savedTextCreatedSong, setSavedTextCreatedSong] = useState(null);
    const [isEditingSavedTextWithAI, setIsEditingSavedTextWithAI] = useState(false);
    const [savedTextsSearchQuery, setSavedTextsSearchQuery] = useState("");
    // Состояние для первого таба (написание текста)
    const [textType, setTextType] = useState("ai");
    const [genres, setGenres] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [recipients, setRecipients] = useState([]);
    const [selectedRecipient, setSelectedRecipient] = useState(null);
    const [selectedReason, setSelectedReason] = useState(null);
    const [selectedGenre, setSelectedGenre] = useState(null);
    // Инициализируем язык из localStorage или из пропсов
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        try {
            const savedState = localStorage.getItem(SONG_CREATION_STATE_STORAGE_KEY);
            if (savedState) {
                const state = JSON.parse(savedState);
                if (state.selectedLanguage) {
                    return state.selectedLanguage;
                }
            }
        }
        catch (error) {
            console.error("Error reading saved language:", error);
        }
        return language;
    });
    const [about, setAbout] = useState("");
    const [customReason, setCustomReason] = useState("");
    const [customText, setCustomText] = useState("");
    const [customTitle, setCustomTitle] = useState("");
    const [generatedText, setGeneratedText] = useState("");
    const [generatedTitle, setGeneratedTitle] = useState("");
    /** Поля результата ИИ показываем только после успешной генерации текста в этом табе */
    const [hasCompletedAiTextGeneration, setHasCompletedAiTextGeneration] = useState(false);
    const [isGeneratingText, setIsGeneratingText] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [moods, setMoods] = useState([]);
    const [instruments, setInstruments] = useState([]);
    const [selectedMoods, setSelectedMoods] = useState([]);
    const [selectedInstruments, setSelectedInstruments] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [extraDescription, setExtraDescription] = useState("");
    // Референс стиля (приложенный трек/видео, уже проанализированный бэком).
    const [styleReference, setStyleReference] = useState(null);
    const [isAnalyzingReference, setIsAnalyzingReference] = useState(false);
    const [createdSong, setCreatedSong] = useState(null);
    // Переход на экран настроек песни (таб «Музыка»/melody).
    useEffect(() => {
        if (activeTab === "melody") {
            trackEvent(AnalyticsEvent.SONG_SETTINGS_OPEN, {
                text_id: createdSong?.text_id ?? createdSong?.id,
            });
            ymReachGoalOnce("song_settings_open", { mode: "classic" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);
    const [editPrompt, setEditPrompt] = useState("");
    const [isEditingWithAI, setIsEditingWithAI] = useState(false);
    const [isEditingDirect, setIsEditingDirect] = useState(false);
    // Состояние для второго таба (выбор мелодии)
    const [selectedVoice, setSelectedVoice] = useState("m");
    const [isCreatingSong, setIsCreatingSong] = useState(false);
    // Голос вокалиста (V): сохранённые профили и выбранный id. Выбор профиля
    // переопределяет обычный VoiceType при генерации. Доступно на всех платформах.
    const [voiceProfiles, setVoiceProfiles] = useState([]);
    const [selectedVoiceProfileId, setSelectedVoiceProfileId] = useState(null);
    // Пока на странице генерации есть список голосов — пользователь ждёт/выбирает
    // свой голос здесь; приходящие «голос готов» помечаем прочитанными (не копим
    // их в бейдже, результат и так виден в списке).
    useAutoReadVoiceReady(voiceProfiles.length > 0);
    // Загрузка сохранённых голосов. Ошибку глушим — селектор просто не покажет
    // блок «Мой голос».
    useEffect(() => {
        let cancelled = false;
        listVoiceProfiles()
            .then((list) => {
            if (!cancelled)
                setVoiceProfiles(list);
        })
            .catch(() => {
            /* нет доступа/ошибка — скрываем блок */
        });
        return () => {
            cancelled = true;
        };
    }, []);
    // Преселект голоса по deep-link `?voice=<id>` (переход из уведомления «Голос
    // готов»). Ждём асинхронной загрузки профилей; выставляем один раз, только
    // если профиль найден и READY. Не-READY/не найден — игнорируем без ошибок.
    const hasPreselectedVoice = useRef(false);
    useEffect(() => {
        if (hasPreselectedVoice.current || voiceProfiles.length === 0)
            return;
        const raw = new URLSearchParams(location.search).get("voice");
        if (!raw)
            return;
        const voiceId = Number(raw);
        if (!Number.isFinite(voiceId))
            return;
        const profile = voiceProfiles.find((p) => p.id === voiceId);
        if (profile && isVoiceProfileReady(profile)) {
            setSelectedVoiceProfileId(voiceId);
            hasPreselectedVoice.current = true;
        }
    }, [voiceProfiles, location.search]);
    // Deep-link `?voice_verify=<id>` (уведомление «Прочитайте контрольную фразу»):
    // открываем менеджер «Мой голос» с продолжением флоу для профиля <id> на шаге
    // пения фразы. Параметр очищаем сразу (чтобы не сработало повторно на F5).
    const [voiceStudioOpen, setVoiceStudioOpen] = useState(false);
    const [voiceVerifyId, setVoiceVerifyId] = useState(null);
    // Гард по значению параметра, а не булев (см. подробности у `?text=` ниже):
    // переход из центра уведомлений идёт через router, ремаунта нет — булев флаг
    // делал бы второе такое уведомление no-op'ом.
    const consumedVoiceVerifyParamRef = useRef(null);
    useEffect(() => {
        const raw = new URLSearchParams(location.search).get("voice_verify");
        if (!raw) {
            consumedVoiceVerifyParamRef.current = null;
            return;
        }
        if (consumedVoiceVerifyParamRef.current === raw)
            return;
        consumedVoiceVerifyParamRef.current = raw;
        const id = Number(raw);
        if (Number.isFinite(id) && id > 0) {
            setVoiceVerifyId(id);
            setVoiceStudioOpen(true);
        }
        // Убираем параметр из URL, сохраняя путь (voice_verify — одноразовый триггер).
        const params = new URLSearchParams(location.search);
        params.delete("voice_verify");
        const nextSearch = params.toString();
        navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, {
            replace: true,
        });
    }, [location.search, location.pathname, navigate]);
    // Deep-link `?text=<id>` (уведомление «Текст готов»): открываем таб «Текст» →
    // источник «Сохранённые» и выделяем именно этот текст. Список «Сохранённых»
    // грузится порциями по 100 (searchTexts), поэтому нужного текста в нём может
    // не оказаться — тянем его точечно (getTextById) и подставляем сами; заодно
    // это снимает гонку с асинхронной загрузкой списка. Параметр одноразовый:
    // убираем его из URL, чтобы не сработало повторно на F5.
    //
    // ПРИОРИТЕТ НАД ЧЕРНОВИКОМ. Эффект восстановления черновика (ниже) пишет
    // activeTab из localStorage, а там у всех, кто дошёл до шага мелодии, лежит
    // "melody" — и он бы перебил наш "text". Полагаться на порядок объявления
    // эффектов нельзя: он ничего не говорит о смысловом приоритете и ломается от
    // любой перестановки. Поэтому факт «таб задан deep-link'ом» фиксируем на
    // ПЕРВОМ РЕНДЕРЕ (до выполнения любого эффекта), а восстановление черновика
    // этот флаг уважает. Новый эффект, который тоже трогает activeTab на маунте,
    // обязан свериться с ним — но НЕ обязан вставать в правильное место файла.
    const [tabOwnedByDeepLink] = useState(() => new URLSearchParams(location.search).has("text"));
    // Гард — не булев флаг «уже обработали», а «какой параметр уже съели»: центр
    // уведомлений навигирует через router (`go` → navigate), поэтому при клике по
    // второму уведомлению «Текст готов» страница НЕ перемонтируется, меняется
    // только query. Одноразовый флаг делал бы такой клик no-op'ом (и параметры
    // висели бы в URL до F5). Сбрасываем гард, как только `text` из URL пропал,
    // — тогда и повторный клик по тому же уведомлению срабатывает снова.
    const consumedTextParamRef = useRef(null);
    useEffect(() => {
        const raw = new URLSearchParams(location.search).get("text");
        if (!raw) {
            consumedTextParamRef.current = null;
            return;
        }
        if (consumedTextParamRef.current === raw)
            return;
        consumedTextParamRef.current = raw;
        const textId = Number(raw);
        const params = new URLSearchParams(location.search);
        params.delete("text");
        const nextSearch = params.toString();
        navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, {
            replace: true,
        });
        if (!Number.isFinite(textId) || textId <= 0)
            return;
        setActiveTab("text");
        setTextSourceType("saved");
        void (async () => {
            try {
                const text = await getTextById(textId);
                const saved = {
                    id: text.id,
                    title: text.title ?? null,
                    text: text.text,
                    created_at: text.created_at,
                    recipient: text.recipient ?? null,
                    reason: text.reason ?? null,
                    language: text.language === "en" ? "en" : "ru",
                    about: text.about ?? null,
                };
                setSavedTexts((prev) => prev.some((item) => item.id === saved.id) ? prev : [saved, ...prev]);
                setSelectedSavedText(saved);
            }
            catch (error) {
                console.error("Error opening text from notification:", error);
                // Текста нет — штатный случай для `text_failed`: бэк шлёт тот же
                // `/generate-song?text=<id>`, а строки в БД не появилось. Молча
                // оставлять пользователя на пустых «Сохранённых» нельзя: возвращаем
                // источник «Новый текст» (можно сразу сгенерировать заново) и
                // объясняем, что произошло.
                setTextSourceType("new");
                const notFound = isTextNotFoundError(error);
                notifications.show({
                    title: notFound
                        ? language === "ru"
                            ? "Текст не найден"
                            : "Lyrics not found"
                        : language === "ru"
                            ? "Ошибка"
                            : "Error",
                    message: notFound
                        ? language === "ru"
                            ? "Текст не сохранился — похоже, генерация не удалась. Попробуйте создать его заново."
                            : "The lyrics were not saved — the generation seems to have failed. Try creating them again."
                        : language === "ru"
                            ? "Не удалось открыть текст из уведомления."
                            : "Failed to open the lyrics from the notification.",
                    color: "red",
                });
            }
        })();
    }, [location.search, location.pathname, navigate, language]);
    // Состояние для третьего таба (генерация)
    const [isGeneratingMusic, setIsGeneratingMusic] = useState(false);
    // Флаги успешного завершения этапов
    const [isTextStepCompleted, setIsTextStepCompleted] = useState(false);
    const [isMelodyStepCompleted, setIsMelodyStepCompleted] = useState(false);
    const [isGenerationStepCompleted, setIsGenerationStepCompleted] = useState(false);
    // Флаг для отслеживания первой загрузки и восстановления состояния
    const isInitialMount = useRef(true);
    const isRestoringState = useRef(false);
    const hasRestoredOptions = useRef(false);
    // Подавление первого дебаунс-запроса после переключения на "Мои тексты"
    const suppressNextSavedDebounce = useRef(false);
    // Единое окно авторизации открывается через App (openRegisterModal).
    const isDesktopAuth = useMediaQuery("(min-width: 771px)");
    const setIsRegisterOpen = (open) => {
        if (open)
            openRegisterModal();
    };
    const prevIsAuthenticatedRef = useRef(null);
    // Возврат после оплаты: открыть экран настроек песни и не сбрасывать его при
    // переходе гость→аккаунт (см. эффект wasAuth ниже).
    const resumeSongSettingsRef = useRef(false);
    // После оплаты сразу запускаем создание песни (без повторного клика), как
    // только восстановится черновик по pendingMelodySongId.
    const autoCreateAfterPaymentRef = useRef(false);
    const createdSongRef = useRef(null);
    // «Повторить генерацию»: снимок настроек исходной песни, переданный из «Моей
    // музыки» через navigation-state. Приоритетнее черновика в localStorage —
    // сразу открываем экран настроек стиля с предзаполнением. Снимок берём один
    // раз на маунт (как resumeSongSettings). Названия жанра/настроений/инструментов
    // мапятся к опциям каталога отдельным эффектом (после его загрузки).
    const repeatSongSettingsRef = useRef((location.state
        ?.repeatSongSettings) ?? null);
    const hasAppliedRepeatOptions = useRef(false);
    // «Повторить генерацию»: жанр/настроения/инструменты применяются только после
    // загрузки каталога. Пока не применили — блокируем «Создать песню», иначе
    // клик до маппинга уйдёт в generate-music без стиля.
    const [repeatOptionsPending, setRepeatOptionsPending] = useState(Boolean(repeatSongSettingsRef.current));
    const customTextRef = useRef(customText);
    const customTitleRef = useRef(customTitle);
    const textTypeRef = useRef(textType);
    useLayoutEffect(() => {
        if (location.pathname !== "/generate-song") {
            return;
        }
        scrollGenerateSongPageToTop();
        const id = requestAnimationFrame(() => scrollGenerateSongPageToTop());
        return () => cancelAnimationFrame(id);
    }, [location.pathname, location.key]);
    useLayoutEffect(() => {
        if (activeTab !== "melody") {
            return;
        }
        scrollGenerateSongPageToTop();
        const id = requestAnimationFrame(() => scrollGenerateSongPageToTop());
        return () => cancelAnimationFrame(id);
    }, [activeTab]);
    // Функция для сохранения состояния
    const saveState = () => {
        try {
            // Получаем текущее сохраненное состояние
            const currentSavedState = localStorage.getItem(SONG_CREATION_STATE_STORAGE_KEY);
            let previousState = null;
            if (currentSavedState) {
                try {
                    previousState = JSON.parse(currentSavedState);
                }
                catch (e) {
                    // Игнорируем ошибки парсинга
                }
            }
            // Сохраняем значения, но не перезаписываем существующие на null
            // если они были установлены ранее
            const stateToSave = {
                textType,
                selectedRecipient: selectedRecipient
                    ? {
                        id: selectedRecipient.id,
                        title: selectedRecipient.title,
                    }
                    : previousState?.selectedRecipient || null,
                selectedReason: selectedReason
                    ? {
                        id: selectedReason.id,
                        title: selectedReason.title,
                    }
                    : previousState?.selectedReason || null,
                selectedGenre: selectedGenre
                    ? {
                        id: selectedGenre.id,
                        title: selectedGenre.title,
                    }
                    : previousState?.selectedGenre || null,
                selectedLanguage,
                about,
                customText,
                customTitle,
                // Не сохраняем generatedText и generatedTitle
                selectedVoice,
                // Настройки песни (нужны, чтобы экран был заполнен как до оплаты).
                selectedMoods: selectedMoods.length > 0
                    ? selectedMoods.map((m) => ({ id: m.id, title: m.title }))
                    : previousState?.selectedMoods || [],
                selectedInstruments: selectedInstruments.length > 0
                    ? selectedInstruments.map((i) => ({ id: i.id, title: i.title }))
                    : previousState?.selectedInstruments || [],
                selectedKey: selectedKey ?? previousState?.selectedKey ?? null,
                extraDescription: extraDescription || previousState?.extraDescription || "",
                // Без фолбэка на previousState: «×» у референса должен переживать F5.
                // Восстановление синхронное (эффект ниже), поэтому ранний save его не затрёт.
                styleReference,
                activeTab,
                isTextStepCompleted,
                isMelodyStepCompleted,
                isGenerationStepCompleted,
                // Часть черновика формы: по нему восстанавливается подготовленный текст.
                // Решение «резюмить ли генерацию после оплаты» на него НЕ опирается —
                // для этого есть маркер намерения (см. pendingSongGeneration).
                pendingMelodySongId: (() => {
                    if (createdSong == null)
                        return null;
                    const n = Number(createdSong.text_id ?? createdSong.id);
                    return Number.isFinite(n) && n > 0 ? n : null;
                })(),
            };
            localStorage.setItem(SONG_CREATION_STATE_STORAGE_KEY, JSON.stringify(stateToSave));
        }
        catch (error) {
            console.error("Error saving state to localStorage:", error);
        }
    };
    // Восстанавливаем состояние при монтировании
    useEffect(() => {
        // «Повторить генерацию» имеет приоритет над черновиком: если пришли настройки
        // исходной песни, восстановление из localStorage пропускаем (см. repeat-эффекты).
        if (repeatSongSettingsRef.current)
            return;
        const savedState = localStorage.getItem(SONG_CREATION_STATE_STORAGE_KEY);
        if (savedState) {
            try {
                const state = JSON.parse(savedState);
                // Восстанавливаем простые значения
                if (state.textType !== undefined)
                    setTextType(state.textType);
                // Восстанавливаем язык (может быть "ru" или "en", оба валидны)
                if (state.selectedLanguage !== undefined) {
                    setSelectedLanguage(state.selectedLanguage);
                }
                if (state.about !== undefined)
                    setAbout(state.about);
                if (state.customText !== undefined)
                    setCustomText(state.customText);
                if (state.customTitle !== undefined)
                    setCustomTitle(state.customTitle);
                // Не восстанавливаем generatedText и generatedTitle
                if (state.selectedVoice !== undefined)
                    setSelectedVoice(state.selectedVoice);
                if (state.selectedKey !== undefined)
                    setSelectedKey(state.selectedKey);
                if (state.extraDescription !== undefined)
                    setExtraDescription(state.extraDescription);
                if (state.styleReference &&
                    typeof state.styleReference.id === "string" &&
                    typeof state.styleReference.description === "string")
                    setStyleReference(state.styleReference);
                // activeTab из черновика НЕ восстанавливаем, если таб уже задан
                // deep-link'ом уведомления (`?text=`): у всех, кто дошёл до шага
                // мелодии, в localStorage лежит "melody", и он перебивал бы «Текст →
                // Сохранённые». Флаг снят на первом рендере — от порядка эффектов не
                // зависит (см. tabOwnedByDeepLink).
                if (state.activeTab !== undefined && !tabOwnedByDeepLink)
                    setActiveTab(state.activeTab);
                if (state.isTextStepCompleted !== undefined)
                    setIsTextStepCompleted(state.isTextStepCompleted);
                if (state.isMelodyStepCompleted !== undefined)
                    setIsMelodyStepCompleted(state.isMelodyStepCompleted);
                if (state.isGenerationStepCompleted !== undefined)
                    setIsGenerationStepCompleted(state.isGenerationStepCompleted);
                // Не восстанавливаем createdSong
            }
            catch (error) {
                console.error("Error restoring state from localStorage:", error);
            }
        }
    }, []);
    // Текст с Welcome: поле «Кому:» в табе ИИ
    useEffect(() => {
        const state = location.state;
        if (state != null &&
            typeof state === "object" &&
            Object.prototype.hasOwnProperty.call(state, "prefillAiAbout")) {
            setAbout(typeof state.prefillAiAbout === "string" ? state.prefillAiAbout : "");
            setTextType("ai");
            setTextSourceType("new");
            setActiveTab("text");
            navigate(`${location.pathname}${location.search}`, {
                replace: true,
                state: {},
            });
        }
    }, [location.state, location.pathname, location.search, navigate]);
    // Восстанавливаем выбранные опции после загрузки списков
    useEffect(() => {
        // При «Повторить генерацию» опции восстанавливает отдельный repeat-эффект.
        if (repeatSongSettingsRef.current)
            return;
        // Восстанавливаем только один раз, когда списки загружены
        if (!isLoadingOptions &&
            genres.length > 0 &&
            reasons.length > 0 &&
            recipients.length > 0 &&
            !hasRestoredOptions.current) {
            hasRestoredOptions.current = true; // Помечаем, что восстановление началось
            try {
                isRestoringState.current = true; // Устанавливаем флаг восстановления
                const savedState = localStorage.getItem(SONG_CREATION_STATE_STORAGE_KEY);
                if (savedState) {
                    const state = JSON.parse(savedState);
                    // Восстанавливаем выбранные опции из загруженных списков
                    // Используем проверку на существование и валидность id
                    // Сравниваем id как строки, чтобы избежать проблем с типами
                    if (state.selectedRecipient &&
                        state.selectedRecipient.id !== undefined &&
                        state.selectedRecipient.id !== null) {
                        const savedId = String(state.selectedRecipient.id);
                        const recipient = recipients.find((r) => String(r.id) === savedId);
                        if (recipient) {
                            setSelectedRecipient(recipient);
                        }
                        else {
                            console.warn("Recipient not found in list. Saved id:", state.selectedRecipient.id, "Available ids:", recipients.map((r) => r.id));
                        }
                    }
                    else {
                        console.log("No saved recipient to restore");
                    }
                    if (state.selectedReason &&
                        state.selectedReason.id !== undefined &&
                        state.selectedReason.id !== null) {
                        const savedId = String(state.selectedReason.id);
                        const reason = reasons.find((r) => String(r.id) === savedId);
                        if (reason) {
                            setSelectedReason(reason);
                        }
                        else {
                            console.warn("Reason not found in list. Saved id:", state.selectedReason.id, "Available ids:", reasons.map((r) => r.id));
                        }
                    }
                    else {
                        console.log("No saved reason to restore");
                    }
                    if (state.selectedGenre &&
                        state.selectedGenre.id !== undefined &&
                        state.selectedGenre.id !== null) {
                        const savedId = String(state.selectedGenre.id);
                        const genre = genres.find((g) => String(g.id) === savedId);
                        if (genre) {
                            setSelectedGenre(genre);
                        }
                        else {
                            console.warn("Genre not found in list. Saved id:", state.selectedGenre.id, "Available ids:", genres.map((g) => g.id));
                        }
                    }
                    else {
                        console.log("No saved genre to restore");
                    }
                    if (Array.isArray(state.selectedMoods) && moods.length > 0) {
                        const ids = new Set(state.selectedMoods.map((m) => String(m.id)));
                        const restoredMoods = moods.filter((m) => ids.has(String(m.id)));
                        if (restoredMoods.length > 0)
                            setSelectedMoods(restoredMoods);
                    }
                    if (Array.isArray(state.selectedInstruments) &&
                        instruments.length > 0) {
                        const ids = new Set(state.selectedInstruments.map((i) => String(i.id)));
                        const restoredInstruments = instruments.filter((i) => ids.has(String(i.id)));
                        if (restoredInstruments.length > 0)
                            setSelectedInstruments(restoredInstruments);
                    }
                    if (state.selectedLanguage) {
                        console.log("Restored language:", state.selectedLanguage);
                    }
                }
                else {
                    console.log("No saved state found in localStorage");
                }
            }
            catch (error) {
                console.error("Error restoring selected options:", error);
            }
            finally {
                // Сбрасываем флаг восстановления после небольшой задержки
                // чтобы дать время всем setState выполниться
                setTimeout(() => {
                    isRestoringState.current = false;
                    console.log("Restoration complete, saving enabled");
                }, 500);
            }
        }
    }, [isLoadingOptions, genres, reasons, recipients]);
    // Загружаем сохраненные тексты при выборе "Мои тексты"
    const loadSavedTexts = async (searchQuery) => {
        setIsLoadingSavedTexts(true);
        try {
            const response = await searchTexts({
                search: searchQuery || null,
                limit: 100,
                offset: 0,
            });
            setSavedTexts(response.results);
            // Поиск по своим текстам: шлём только при реальном запросе. Сам текст
            // запроса НЕ шлём (PII) — только его длину и кол-во результатов.
            const q = searchQuery?.trim();
            if (q) {
                trackEvent(AnalyticsEvent.TEXT_SEARCH, {
                    query_len: q.length,
                    results_count: response.results.length,
                });
            }
        }
        catch (error) {
            console.error("Error loading saved texts:", error);
        }
        finally {
            setIsLoadingSavedTexts(false);
        }
    };
    useEffect(() => {
        if (textSourceType === "saved" && canUseTexts) {
            // Загружаем сразу при открытии таба и подавляем ближайший дебаунс-вызов
            suppressNextSavedDebounce.current = true;
            loadSavedTexts(savedTextsSearchQuery);
        }
    }, [textSourceType, canUseTexts]);
    // Debounce для поиска
    useEffect(() => {
        if (textSourceType === "saved" && canUseTexts) {
            // Пропускаем первый дебаунс после переключения таба
            if (suppressNextSavedDebounce.current) {
                suppressNextSavedDebounce.current = false;
                return;
            }
            const timeoutId = setTimeout(() => {
                loadSavedTexts(savedTextsSearchQuery);
            }, 500); // Задержка 500мс
            return () => clearTimeout(timeoutId);
        }
    }, [savedTextsSearchQuery, textSourceType, canUseTexts]);
    useEffect(() => {
        const wasAuth = prevIsAuthenticatedRef.current;
        prevIsAuthenticatedRef.current = isAuthenticated;
        // Возврат после оплаты / «Повторить генерацию» — экран настроек оставляем
        // (не сбрасываем на «текст»).
        if (resumeSongSettingsRef.current || repeatSongSettingsRef.current) {
            return;
        }
        if (wasAuth === false && isAuthenticated && activeTab === "melody") {
            setActiveTab("text");
        }
    }, [isAuthenticated, activeTab]);
    // Возврат из оплаты в флоу создания песни: открыть экран настроек песни.
    // Заполнение восстановят обычные эффекты (saveState/restore + rehydrate по
    // pendingMelodySongId). Флаг в navigation state очищаем, чтобы не сработало
    // повторно при обновлении страницы.
    useEffect(() => {
        const navState = location.state;
        if (navState?.resumeSongSettings) {
            resumeSongSettingsRef.current = true;
            // Автозапуск создания после оплаты — сработает, когда восстановится песня.
            autoCreateAfterPaymentRef.current = true;
            setActiveTab("melody");
            navigate(`${location.pathname}${location.search}`, {
                replace: true,
                state: {},
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // «Повторить генерацию»: сразу открываем экран настроек стиля исходной песни.
    // Скалярные поля (голос, тональность, доп.описание, текст/название) и сама
    // песня (для text_id) выставляются здесь; жанр/настроения/инструменты мапятся
    // к каталогу отдельным эффектом ниже, когда он загрузится. Черновик в
    // localStorage чистим — «повторить» его перетирает. Флаг из navigation-state
    // снимаем, чтобы не сработало повторно на F5.
    useEffect(() => {
        const src = repeatSongSettingsRef.current;
        if (!src)
            return;
        clearSongCreationPersistedState();
        // Исходная песня даёт text_id — генерация повторится на том же тексте.
        setCreatedSong(src);
        createdSongRef.current = src;
        setIsTextStepCompleted(true);
        if (src.voice)
            setSelectedVoice(src.voice);
        setSelectedKey(src.key ? src.key : null);
        setExtraDescription(src.extra_description ?? "");
        // Текст/название — на случай возврата на шаг текста (генерация всё равно
        // идёт по text_id исходной песни).
        setCustomText(src.final_text ?? src.text ?? "");
        setCustomTitle(src.title ?? "");
        setTextType("custom");
        setActiveTab("melody");
        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: {},
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // «Повторить генерацию»: сопоставляем названия жанра/настроений/инструментов
    // исходной песни с опциями каталога (селекты матчат по объектам каталога,
    // а generate-music шлёт их title). Выполняется один раз, когда каталог готов.
    useEffect(() => {
        const src = repeatSongSettingsRef.current;
        if (!src)
            return;
        if (isLoadingOptions || hasAppliedRepeatOptions.current)
            return;
        if (genres.length === 0 && moods.length === 0 && instruments.length === 0)
            return;
        // И каталог, и сохранённые значения содержат ведущий эмодзи, но на всякий
        // случай снимаем его с обеих сторон — так матчинг не рассыпается, если бэк
        // где-то нормализовал/обрезал title. Кросс-язык (RU-песня ↔ EN-каталог)
        // по названиям всё равно не сопоставить — это ограничение фичи.
        const norm = (s) => stripLeadEmoji(s).trim().toLowerCase();
        // genre в модели — строка, но подстрахуемся от объекта {title} как у moods.
        const genreRaw = src.genre;
        const genreTitle = (typeof genreRaw === "string"
            ? genreRaw
            : genreRaw?.title ?? "").trim();
        if (genreTitle) {
            const genre = genres.find((g) => norm(g.title) === norm(genreTitle));
            if (genre)
                setSelectedGenre(genre);
        }
        const moodTitles = toTitleList(src.moods);
        if (moodTitles.length > 0) {
            const wanted = new Set(moodTitles.map(norm));
            const restored = moods.filter((m) => wanted.has(norm(m.title)));
            if (restored.length > 0)
                setSelectedMoods(restored);
        }
        const instrumentTitles = toTitleList(src.instruments);
        if (instrumentTitles.length > 0) {
            const wanted = new Set(instrumentTitles.map(norm));
            const restored = instruments.filter((i) => wanted.has(norm(i.title)));
            if (restored.length > 0)
                setSelectedInstruments(restored);
        }
        // Флаг ставим В КОНЦЕ (после применения), чтобы частичный сбой не оставил
        // маппинг наполовину применённым без возможности повтора.
        hasAppliedRepeatOptions.current = true;
        setRepeatOptionsPending(false);
    }, [isLoadingOptions, genres, moods, instruments]);
    useEffect(() => {
        createdSongRef.current = createdSong;
    }, [createdSong]);
    useEffect(() => {
        customTextRef.current = customText;
        customTitleRef.current = customTitle;
        textTypeRef.current = textType;
    }, [customText, customTitle, textType]);
    useEffect(() => {
        // Гость тоже восстанавливает черновик песни (в т.ч. после оплаты).
        if (!isAuthenticated && !isGuest)
            return;
        const saved = localStorage.getItem(SONG_CREATION_STATE_STORAGE_KEY);
        if (!saved)
            return;
        let state;
        try {
            state = JSON.parse(saved);
        }
        catch {
            return;
        }
        const raw = state.pendingMelodySongId;
        if (raw == null || raw === "")
            return;
        const id = Number(raw);
        if (!Number.isFinite(id) || id <= 0)
            return;
        const currentId = createdSong != null
            ? Number(createdSong.text_id ?? createdSong.id)
            : null;
        if (currentId === id)
            return;
        let cancelled = false;
        (async () => {
            try {
                const s = await getSongById(id);
                if (cancelled)
                    return;
                setCreatedSong(s);
                createdSongRef.current = s;
            }
            catch {
                if (cancelled)
                    return;
                setActiveTab("text");
                setIsTextStepCompleted(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, isGuest, createdSong?.id, createdSong?.text_id]);
    // Сохраняем состояние при изменении настроек
    // НЕ сохраняем при первой загрузке или во время восстановления состояния
    useEffect(() => {
        // Пропускаем сохранение при первой загрузке
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        // Пропускаем сохранение во время восстановления состояния
        if (isRestoringState.current) {
            return;
        }
        // Возврат после оплаты: пока черновик песни не восстановлен по
        // pendingMelodySongId (createdSong ещё пустой), НЕ сохраняем — смена
        // activeTab на «melody» иначе затёрла бы pendingMelodySongId в null, и
        // резюм/автозапуск создания сорвался бы.
        if (resumeSongSettingsRef.current && createdSong == null) {
            return;
        }
        saveState();
    }, [
        textType,
        selectedRecipient,
        selectedReason,
        selectedGenre,
        selectedLanguage,
        about,
        customText,
        customTitle,
        generatedText,
        generatedTitle,
        selectedVoice,
        selectedMoods,
        selectedInstruments,
        selectedKey,
        extraDescription,
        styleReference,
        activeTab,
        isTextStepCompleted,
        isMelodyStepCompleted,
        isGenerationStepCompleted,
        createdSong,
    ]);
    useEffect(() => {
        const loadOptions = async () => {
            setIsLoadingOptions(true);
            // Сбрасываем флаг восстановления при перезагрузке опций (например, при смене языка)
            hasRestoredOptions.current = false;
            try {
                const [genresData, reasonsData, recipientsData, moodsData, instrumentsData,] = await Promise.all([
                    searchGenres({ limit: 100, language: catalogLanguage }),
                    searchReasons({ limit: 100, language: catalogLanguage }),
                    searchRecipients({ limit: 100, language: catalogLanguage }),
                    searchMoods({ limit: 100, language: catalogLanguage }),
                    searchInstruments({ limit: 100, language: catalogLanguage }),
                ]);
                setGenres(genresData || []);
                setReasons(reasonsData || []);
                setRecipients(recipientsData || []);
                setMoods(moodsData || []);
                setInstruments(instrumentsData || []);
            }
            catch (error) {
                console.error("Error loading options:", error);
                notifications.show({
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: language === "ru"
                        ? "Не удалось загрузить данные"
                        : "Failed to load data",
                    color: "red",
                });
            }
            finally {
                setIsLoadingOptions(false);
            }
        };
        loadOptions();
    }, [catalogLanguage]);
    const performGenerateText = async () => {
        if ((!selectedReason && !customReason.trim()) || !about.trim()) {
            notifications.show({
                title: language === "ru" ? "Ошибка" : "Error",
                message: language === "ru"
                    ? "Заполните все поля"
                    : "Please fill in all fields",
                color: "red",
            });
            return;
        }
        setIsGeneratingText(true);
        setGlobalIsGeneratingText(true); // Устанавливаем глобальное состояние
        try {
            const requestData = {
                about: about,
                language: selectedLanguage,
                // Блок «Кому песня» убран из UI — в запрос уходит прочерк.
                recipient: "—",
                reason: customReason.trim() || selectedReason?.title || "",
            };
            // Воронка: старт ИИ-генерации текста (клик «Сгенерировать»). Завершение
            // (text_generated) пишет бэк. about_len — длина брифа, не сам текст (PII).
            trackEvent(AnalyticsEvent.TEXT_GEN_START, {
                occasion: requestData.reason,
                recipient: requestData.recipient,
                about_len: about.length,
                language: selectedLanguage,
            });
            ymReachGoalOnce("text_gen_start", { mode: "classic" });
            // Создаем задачу на генерацию текста и получаем ws_event_id
            const { ws_event_id } = await createNewSong(requestData);
            // Ждем результат через WebSocket
            const song = await waitForSongGeneration(ws_event_id);
            const songText = song.text || "";
            const songTitle = song.title || "";
            setGeneratedText(songText);
            setGeneratedTitle(songTitle);
            setCreatedSong(song);
            setHasCompletedAiTextGeneration(true);
            // Обновляем данные пользователя (баланс) после успешного создания текста.
            // Гость теперь тоже валиден на /users/me (200 + is_guest), поэтому
            // обновляем и ему — иначе баланс/лимиты после генерации не подтянутся.
            if (isAuthenticated || isGuest) {
                await refreshUser();
            }
            // Прокручиваем к результату
            setTimeout(() => {
                const resultCard = document.querySelector("[data-generated-result]");
                if (resultCard) {
                    resultCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
            }, 100);
            notifications.show({
                title: language === "ru" ? "Успешно" : "Success",
                message: language === "ru"
                    ? "Текст успешно сгенерирован"
                    : "Text generated successfully",
                color: "green",
            });
        }
        catch (error) {
            console.error("Error generating text:", error);
            const errorMessage = resolveTextActionErrorMessage(error, language, "Ошибка при генерации текста", "Error generating text");
            notifications.show({
                title: language === "ru" ? "Ошибка" : "Error",
                message: errorMessage,
                color: "red",
            });
        }
        finally {
            setIsGeneratingText(false);
            setGlobalIsGeneratingText(false); // Сбрасываем глобальное состояние
        }
    };
    const handleGenerateText = async () => {
        // Генерация текста и выбор жанра/настроения доступны без авторизации.
        // Модалка регистрации поднимается только на «Создать песню»
        // (handleCreateSong), т.к. генерация музыки требует аккаунт.
        await performGenerateText();
    };
    const handleContinueWithText = async () => {
        if (textType === "ai") {
            const lyricsToContinue = generatedText.trim() || createdSong?.text?.trim() || "";
            if (!lyricsToContinue) {
                notifications.show({
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: language === "ru"
                        ? "Сначала сгенерируйте текст"
                        : "Please generate text first",
                    color: "red",
                });
                return;
            }
            if (!createdSong || !createdSong.id) {
                notifications.show({
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: language === "ru" ? "Песня не создана" : "Song not created",
                    color: "red",
                });
                return;
            }
            // Используем edit-direct для обновления текста (синхронный эндпоинт)
            setIsGeneratingText(true);
            try {
                // Если text_id отсутствует, используем id (для обратной совместимости)
                const textId = createdSong.text_id ?? createdSong.id;
                if (textId == null) {
                    notifications.show({
                        title: language === "ru" ? "Ошибка" : "Error",
                        message: language === "ru" ? "ID текста не найден" : "Text ID not found",
                        color: "red",
                    });
                    return;
                }
                const updatedSong = await editSongDirect(textId, {
                    text: generatedText || createdSong.text || "",
                    title: generatedTitle.trim() ||
                        createdSong.title?.trim() ||
                        undefined,
                });
                // Ответ edit-direct может не содержать text_id — сохраняем из предыдущего состояния
                setCreatedSong({
                    ...createdSong,
                    ...updatedSong,
                    text_id: updatedSong.text_id ??
                        createdSong.text_id ??
                        updatedSong.id ??
                        createdSong.id,
                    id: updatedSong.id ?? createdSong.id,
                });
                setGeneratedText(updatedSong.text || "");
                if (updatedSong.title) {
                    setGeneratedTitle(updatedSong.title);
                }
                // Отмечаем первый этап как завершенный
                setIsTextStepCompleted(true);
                // Переходим на второй таб для выбора голоса
                setActiveTab("melody");
            }
            catch (error) {
                console.error("Error updating song text:", error);
                const errorMessage = error.response?.data?.detail ||
                    error.response?.data?.message ||
                    error.message ||
                    (language === "ru"
                        ? "Ошибка при обновлении текста"
                        : "Error updating text");
                notifications.show({
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: errorMessage,
                    color: "red",
                });
            }
            finally {
                setIsGeneratingText(false);
                setGlobalIsGeneratingText(false);
            }
        }
        else if (textType === "custom") {
            if (!customText.trim()) {
                notifications.show({
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: language === "ru"
                        ? "Введите текст песни"
                        : "Please enter song text",
                    color: "red",
                });
                return;
            }
            const runCustomTextContinue = async () => {
                setIsCreatingSong(true);
                // Ввод своего текста (без LLM). Завершение (text_created_manual) пишет
                // бэк. text_len — длина, не сам текст (PII).
                trackEvent(AnalyticsEvent.TEXT_MANUAL_INPUT, {
                    text_len: customText.length,
                    language: selectedLanguage,
                });
                try {
                    const { ws_event_id } = await createSongFromCustomText({
                        text: customText,
                        title: customTitle.trim() || undefined,
                        recipient: null,
                        reason: null,
                    });
                    const song = await waitForSongGeneration(ws_event_id);
                    setCreatedSong(song);
                    setIsTextStepCompleted(true);
                    setActiveTab("melody");
                }
                catch (error) {
                    console.error("Error creating song from custom text:", error);
                    const errorMessage = resolveTextActionErrorMessage(error, language, "Ошибка при создании песни", "Error creating song");
                    notifications.show({
                        title: language === "ru" ? "Ошибка" : "Error",
                        message: errorMessage,
                        color: "red",
                    });
                }
                finally {
                    setIsCreatingSong(false);
                }
            };
            // Без авторизации: пользователь готовит текст и переходит к выбору
            // жанра/настроения. Регистрация спрашивается позже, на «Создать песню».
            await runCustomTextContinue();
        }
    };
    const performCreateSongAfterAuth = async () => {
        setIsCreatingSong(true);
        try {
            let song = createdSongRef.current ?? createdSong;
            if (song) {
                createdSongRef.current = song;
            }
            if (!song &&
                textTypeRef.current === "custom" &&
                customTextRef.current.trim()) {
                const { ws_event_id } = await createSongFromCustomText({
                    text: customTextRef.current,
                    title: customTitleRef.current.trim() || undefined,
                    recipient: null,
                    reason: null,
                });
                song = await waitForSongGeneration(ws_event_id);
                setCreatedSong(song);
                createdSongRef.current = song;
                setIsTextStepCompleted(true);
            }
            if (!song) {
                notifications.show({
                    title: language === "ru" ? "Внимание" : "Notice",
                    message: language === "ru"
                        ? "Сначала подготовьте текст на шаге «Создание текста», затем снова нажмите «Создать песню»"
                        : "Prepare your lyrics on the first step, then tap «Create Song» again",
                    color: "orange",
                });
                return;
            }
            setIsMelodyStepCompleted(true);
            await handleGenerateMusic(song);
            await refreshUser();
        }
        catch (error) {
            // Нехватка баланса (402) — попап пополнения уже открыт интерсептором.
            if (isInsufficientBalanceError(error)) {
                return;
            }
            console.error("Error creating song:", error);
            const errorMessage = resolveTextActionErrorMessage(error, language, "Ошибка при создании песни", "Error creating song");
            notifications.show({
                title: language === "ru" ? "Ошибка" : "Error",
                message: errorMessage,
                color: "red",
            });
        }
        finally {
            setIsCreatingSong(false);
        }
    };
    const handleCreateSong = async () => {
        let song = createdSongRef.current ?? createdSong;
        if (song) {
            createdSongRef.current = song;
        }
        const hasCustomDraft = textType === "custom" && customText.trim().length > 0;
        if (!song && hasCustomDraft) {
            if (!isAuthenticated) {
                schedulePostEmailAuthAction(() => {
                    void performCreateSongAfterAuth();
                });
                if (isDesktopAuth) {
                    setIsRegisterOpen(true);
                }
                else if (openRegisterModal) {
                    openRegisterModal();
                }
                else {
                    schedulePostEmailAuthAction(null);
                    notifications.show({
                        title: language === "ru" ? "Ошибка" : "Error",
                        message: language === "ru"
                            ? "Необходима авторизация"
                            : "Authentication required",
                        color: "red",
                    });
                }
                return;
            }
            await performCreateSongAfterAuth();
            return;
        }
        if (!song) {
            notifications.show({
                title: language === "ru" ? "Ошибка" : "Error",
                message: language === "ru"
                    ? "Сначала создайте текст песни"
                    : "Create the song text first",
                color: "red",
            });
            return;
        }
        // Гостевой чекаут: гость создаёт песню без регистрации (лимиты/баланс
        // проверяет бэк). Форсируем вход только полностью анонимным (не гость).
        if (!isAuthenticated && !isGuest) {
            schedulePostEmailAuthAction(() => {
                void performCreateSongAfterAuth();
            });
            if (isDesktopAuth) {
                setIsRegisterOpen(true);
            }
            else if (openRegisterModal) {
                openRegisterModal();
            }
            else {
                schedulePostEmailAuthAction(null);
                notifications.show({
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: language === "ru"
                        ? "Необходима авторизация"
                        : "Authentication required",
                    color: "red",
                });
            }
            return;
        }
        await performCreateSongAfterAuth();
    };
    const handleGenerateMusic = async (songOverride) => {
        const song = songOverride ?? createdSong;
        if (!song) {
            notifications.show({
                title: language === "ru" ? "Ошибка" : "Error",
                message: language === "ru" ? "Песня не создана" : "Song not created",
                color: "red",
            });
            return;
        }
        if (!song.id) {
            console.error("song.id is undefined:", song);
            notifications.show({
                title: language === "ru" ? "Ошибка" : "Error",
                message: language === "ru" ? "ID песни отсутствует" : "Song ID is missing",
                color: "red",
            });
            return;
        }
        setIsGeneratingMusic(true);
        try {
            console.log("Generating music for song ID:", song.id);
            // Создаем задачу на генерацию музыки и получаем ws_event_id
            // Если text_id отсутствует, используем id (для обратной совместимости)
            const textId = song.text_id || song.id;
            if (!textId) {
                notifications.show({
                    title: language === "ru" ? "Ошибка" : "Error",
                    message: language === "ru" ? "ID текста не найден" : "Text ID not found",
                    color: "red",
                });
                return;
            }
            // Старт генерации песни (клик «Создать песню» → реальный запуск музыки).
            // Завершение (song_generated) пишет бэк по факту успеха Suno.
            trackEvent(AnalyticsEvent.SONG_GEN_START, {
                text_id: textId,
                genre: selectedGenre?.title || null,
                voice: selectedVoice,
                moods_count: selectedMoods.length,
                instruments_count: selectedInstruments.length,
                has_extra: Boolean(selectedKey) || extraDescription.trim().length > 0,
                has_style_reference: Boolean(styleReference),
            });
            // Та же ступень в Метрике (отдельная от собственной аналитики система,
            // см. yandexMetrika): «пользователь реально запустил песню».
            ymReachGoal("song_gen_start", { mode: "classic" });
            const { ws_event_id } = await generateMusic({
                text_id: textId,
                voice: selectedVoice,
                genre: selectedGenre?.title || null,
                moods: selectedMoods.length > 0 ? selectedMoods.map((m) => m.title) : null,
                instruments: selectedInstruments.length > 0
                    ? selectedInstruments.map((i) => i.title)
                    : null,
                key: selectedKey || null,
                extra_description: extraDescription.trim() || null,
                voice_profile_id: selectedVoiceProfileId ?? undefined,
                style_reference_id: styleReference?.id ?? null,
            });
            // Обновляем данные пользователя сразу после отправки запроса
            await refreshUser();
            setPendingMusicGeneration({
                ws_event_id,
                song_id: song.id,
            });
            // Песня создана → показываем вкладку «Мои песни» в сайдбаре (в т.ч. гостю).
            markHasCreatedSongs();
            const isMiniApp = isTelegramMiniApp() || isMaxMiniApp();
            notifications.show({
                title: language === "ru" ? "Генерация начата" : "Generation started",
                message: isMiniApp
                    ? language === "ru"
                        ? "Мы начали генерировать вашу песню, как только генерация завершится, бот пришлёт вам уведомление"
                        : "We have started generating your song, as soon as the generation is complete, the bot will send you a notification"
                    : language === "ru"
                        ? "Открываем «Мои песни» — там появится новый трек, когда музыка будет готова"
                        : "Opening My Songs — your new track will appear when the music is ready",
                color: "blue",
                autoClose: 5000,
            });
            // Сбрасываем все состояния
            setActiveTab("text");
            setTextType("ai");
            setSelectedRecipient(null);
            setSelectedReason(null);
            setSelectedGenre(null);
            setSelectedLanguage(language);
            setAbout("");
            setCustomText("");
            setCustomTitle("");
            setGeneratedText("");
            setGeneratedTitle("");
            setHasCompletedAiTextGeneration(false);
            setCreatedSong(null);
            setSelectedMoods([]);
            setSelectedInstruments([]);
            setSelectedKey(null);
            setExtraDescription("");
            setStyleReference(null);
            setSelectedVoice("m");
            setIsTextStepCompleted(false);
            setIsMelodyStepCompleted(false);
            setIsGenerationStepCompleted(false);
            setIsGeneratingText(false); // Убеждаемся, что модалка закрыта
            // Очищаем сохраненное состояние из localStorage
            try {
                localStorage.removeItem(SONG_CREATION_STATE_STORAGE_KEY);
            }
            catch (error) {
                console.error("Error clearing localStorage:", error);
            }
            // Генерация запущена — намерение исполнено, маркер больше не нужен.
            clearPendingSongGeneration();
            setIsGeneratingMusic(false);
            navigate("/my-songs", {
                replace: true,
                state: {
                    pendingMusicWsEventId: ws_event_id,
                    pendingMusicSongId: song.id,
                },
            });
        }
        catch (error) {
            // Нехватка баланса (402) — 401-интерсептор уже открыл попап пополнения.
            // Не показываем красную ошибку с сырым detail «Insufficient SONGS balance».
            if (isInsufficientBalanceError(error)) {
                // Единственная точка, где рождается намерение «оплачиваю генерацию»:
                // пользователь нажал «Создать песню» и упёрся в пейвол. Только по этому
                // маркеру возврат с оплаты продолжит генерацию (см. pendingSongGeneration).
                savePendingSongGeneration(Number(song.text_id || song.id));
                setIsGeneratingMusic(false);
                return;
            }
            // Черновик устарел: text_id из прошлой сессии/аккаунта
            // («Text(id=…) not found»). Сбрасываем на шаг текста без красной ошибки.
            if (isTextNotFoundError(error)) {
                try {
                    localStorage.removeItem(SONG_CREATION_STATE_STORAGE_KEY);
                }
                catch {
                    /* ignore */
                }
                setCreatedSong(null);
                setIsTextStepCompleted(false);
                setActiveTab("text");
                setIsGeneratingMusic(false);
                notifications.show({
                    title: language === "ru" ? "Черновик устарел" : "Draft expired",
                    message: language === "ru"
                        ? "Текст из прошлой сессии больше недоступен. Создайте текст заново."
                        : "The text from a previous session is no longer available. Please create the text again.",
                    color: "orange",
                });
                return;
            }
            // Референс протух (живёт 24 ч) или от другого аккаунта: убираем его и
            // просим приложить заново — генерировать «без стиля» молча нельзя.
            if (isStyleReferenceNotFoundError(error)) {
                setStyleReference(null);
                setIsGeneratingMusic(false);
                notifications.show({
                    title: language === "ru" ? "Референс устарел" : "Reference expired",
                    message: language === "ru"
                        ? "Прикрепите трек-референс заново или создайте песню без него."
                        : "Attach the reference track again or create the song without it.",
                    color: "orange",
                });
                return;
            }
            console.error("Error generating music:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                (language === "ru"
                    ? "Ошибка при генерации музыки"
                    : "Error generating music");
            notifications.show({
                title: language === "ru" ? "Ошибка" : "Error",
                message: errorMessage,
                color: "red",
            });
            setIsGeneratingMusic(false);
        }
    };
    // Автозапуск создания песни после возврата с оплаты: как только черновик
    // восстановлен (createdSong по pendingMelodySongId), запускаем создание один
    // раз — без повторного клика «Создать песню».
    useEffect(() => {
        if (!autoCreateAfterPaymentRef.current)
            return;
        if (isCreatingSong || isGeneratingMusic)
            return;
        const song = createdSongRef.current ?? createdSong;
        if (!song)
            return;
        autoCreateAfterPaymentRef.current = false;
        void handleCreateSong();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdSong, isCreatingSong, isGeneratingMusic]);
    const showAiGeneratedResult = hasCompletedAiTextGeneration;
    return (_jsxs("div", { className: styles.container, children: [_jsx(VoiceStudioModal, { opened: voiceStudioOpen, onClose: () => {
                    setVoiceStudioOpen(false);
                    setVoiceVerifyId(null);
                }, language: language, initialVoiceVerifyId: voiceVerifyId }), _jsx(VpModal, { opened: isGeneratingText, onClose: () => { }, size: "sm", hideCloseButton: true, closeOnBackdropClick: false, closeOnEscape: false, ariaLabel: language === "ru"
                    ? "Идёт генерация текста"
                    : "Generating text", children: _jsxs("div", { className: styles.loadingModalBody, children: [_jsx(VpLoader, { size: "xl" }), _jsx("div", { className: styles.loadingModalText, children: language === "ru" ? "Пишем текст песни…" : "Writing lyrics…" }), _jsx("div", { className: styles.loadingModalHint, children: language === "ru"
                                ? "Обычно занимает около минуты. Не закрывайте страницу"
                                : "Usually takes about a minute. Please don't close the page" })] }) }), _jsx(VpModal, { opened: isCreatingSong, onClose: () => { }, size: "sm", hideCloseButton: true, closeOnBackdropClick: false, closeOnEscape: false, ariaLabel: language === "ru" ? "Идёт создание песни" : "Creating song", children: _jsxs("div", { className: styles.loadingModalBody, children: [_jsx(VpLoader, { size: "xl" }), _jsx("div", { className: styles.loadingModalText, children: language === "ru" ? "Записываем песню…" : "Recording song…" }), _jsx("div", { className: styles.loadingModalHint, children: language === "ru"
                                ? "Голос и сведение — это дольше. До минуты."
                                : "Voice and mix take a bit longer. Up to a minute." })] }) }), _jsx(VpModal, { opened: isEditingWithAI || isEditingSavedTextWithAI, onClose: () => { }, size: "sm", hideCloseButton: true, closeOnBackdropClick: false, closeOnEscape: false, ariaLabel: language === "ru" ? "Идёт редактирование" : "Editing", children: _jsxs("div", { className: styles.loadingModalBody, children: [_jsx(VpLoader, { size: "xl" }), _jsx("div", { className: styles.loadingModalText, children: language === "ru" ? "Правим текст…" : "Editing lyrics…" })] }) }), _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 32 }, children: [_jsx(GenerateHeader, { activeTab: activeTab, language: language }), activeTab === "text" && (_jsx("div", { children: (() => {
                            // Лейбл и подписи табов одинаковы для всех трёх подэкранов —
                            // при переключении табов текст не меняется.
                            const sourceSegment = (_jsxs("div", { className: styles.briefField, children: [_jsx("div", { className: styles.briefLabel, children: language === "ru" ? "Источник текста" : "Text source" }), _jsx(VpSegmentedControl, { fullWidth: true, value: textSourceType === "saved" ? "saved" : textType, onChange: (val) => {
                                            if (val === "saved") {
                                                setTextSourceType("saved");
                                                trackEvent(AnalyticsEvent.TEXT_HISTORY_VIEW);
                                            }
                                            else {
                                                setTextSourceType("new");
                                                setTextType(val);
                                                trackEvent(val === "custom"
                                                    ? AnalyticsEvent.TEXT_MANUAL_VIEW
                                                    : AnalyticsEvent.TEXT_AI_VIEW);
                                            }
                                        }, options: [
                                            {
                                                value: "ai",
                                                label: language === "ru" ? "ИИ напишет" : "AI writes",
                                            },
                                            {
                                                value: "custom",
                                                label: language === "ru" ? "Свой" : "Custom",
                                            },
                                            {
                                                value: "saved",
                                                label: language === "ru" ? "Сохранённые" : "Saved",
                                            },
                                        ] })] }));
                            const helpExampleText = language === "ru"
                                ? "Маме на 60 лет. Зовут Елена, добрая, печёт лучшие пироги, всю жизнь работала учителем. Хочется тёплую и душевную песню."
                                : "For mom's 60th birthday. Her name is Elena, she is kind, bakes the best pies, worked as a teacher all her life. We want a warm and heartfelt song.";
                            const helpPanel = (_jsxs("div", { className: styles.helpPanel, children: [_jsx("div", { className: styles.helpCaption, children: language === "ru"
                                            ? "Полезно упомянуть:"
                                            : "Helpful to mention:" }), _jsx("ol", { className: styles.helpList, children: [
                                            language === "ru"
                                                ? ["Имя", " — кому посвящена"]
                                                : ["Name", " — who it's for"],
                                            language === "ru"
                                                ? ["Возраст / повод", " — юбилей, ДР"]
                                                : ["Age / occasion", " — anniversary, birthday"],
                                            language === "ru"
                                                ? ["2–3 черты", " — добрая, упрямый"]
                                                : ["2–3 traits", " — kind, stubborn"],
                                            language === "ru"
                                                ? ["Что любит", " — хобби, привычки"]
                                                : ["What they love", " — hobbies, habits"],
                                            language === "ru"
                                                ? ["Настроение", " — тёплое, ироничное"]
                                                : ["Mood", " — warm, ironic"],
                                        ].map((item, idx) => (_jsxs("li", { className: styles.helpItem, children: [_jsx("span", { className: styles.helpNumber, children: idx + 1 }), _jsxs("span", { children: [_jsx("b", { children: item[0] }), _jsx("span", { className: styles.helpItemRest, children: item[1] })] })] }, idx))) }), _jsx("div", { className: styles.helpCaption, children: language === "ru" ? "Готовый пример:" : "Ready example:" }), _jsx("div", { className: styles.helpExample, children: language === "ru"
                                            ? `«${helpExampleText}»`
                                            : `"${helpExampleText}"` }), _jsx("button", { type: "button", className: styles.helpTemplateBtn, onClick: () => setAbout(helpExampleText), children: language === "ru"
                                            ? "↳ Использовать как шаблон"
                                            : "↳ Use as a template" })] }));
                            return (_jsxs(_Fragment, { children: [textSourceType === "new" && textType === "ai" && (_jsx("div", { children: isLoadingOptions ? (_jsx("div", { style: {
                                                display: "flex",
                                                justifyContent: "center",
                                                padding: "32px 0",
                                            }, children: _jsx(VpLoader, { size: "lg" }) })) : (_jsxs("div", { className: styles.aiGrid, children: [_jsxs("div", { className: styles.aiForm, children: [sourceSegment, _jsxs("div", { className: styles.briefField, children: [_jsx("div", { className: styles.briefLabel, children: language === "ru" ? "Повод" : "Reason" }), _jsx("div", { className: styles.chipRow, children: reasons.map((reason) => (_jsx(IconChip, { selected: selectedReason?.id === reason.id, onClick: () => {
                                                                            // Чип и своё поле «повода» — взаимоисключающие.
                                                                            setSelectedReason(reason);
                                                                            setCustomReason("");
                                                                            trackEvent(AnalyticsEvent.REASON_SELECT, {
                                                                                value: reason.title,
                                                                            });
                                                                        }, children: reason.title }, reason.id))) })] }), _jsx(VpTextField, { value: customReason, onChange: (e) => {
                                                                const value = e.currentTarget.value;
                                                                setCustomReason(value);
                                                                // Печатаем свой повод — снимаем выбор с чипа.
                                                                if (value.trim())
                                                                    setSelectedReason(null);
                                                            }, onBlur: (e) => {
                                                                const v = e.currentTarget.value.trim();
                                                                if (v) {
                                                                    trackEvent(AnalyticsEvent.REASON_CUSTOM_INPUT, {
                                                                        value_len: v.length,
                                                                    });
                                                                }
                                                            }, placeholder: language === "ru"
                                                                ? "или свой повод…"
                                                                : "or your own occasion…" }), _jsxs("div", { className: styles.briefField, children: [_jsx(VpTextarea, { label: language === "ru"
                                                                        ? "О человеке"
                                                                        : "About the person", placeholder: language === "ru"
                                                                        ? "Например: «Папа любит чинить «Ниву», поёт Высоцкого, вышел на пенсию — собирается путешествовать»"
                                                                        : 'e.g., "Dad loves fixing his old car, sings Vysotsky, just retired — plans to travel"', value: about, onChange: (e) => setAbout(e.currentTarget.value), onBlur: (e) => {
                                                                        const v = e.currentTarget.value.trim();
                                                                        if (v) {
                                                                            trackEvent(AnalyticsEvent.ABOUT_INPUT, {
                                                                                about_len: v.length,
                                                                            });
                                                                        }
                                                                    }, autosize: true, rows: 4, maxLength: 600 }), _jsx(Accordion, { defaultOpenId: "help", items: [
                                                                        {
                                                                            id: "help",
                                                                            title: (_jsxs("span", { className: styles.helpToggle, children: [_jsx("span", { className: styles.helpBadge, children: "?" }), language === "ru"
                                                                                        ? "Что писать в описании"
                                                                                        : "What to write in the description"] })),
                                                                            content: helpPanel,
                                                                        },
                                                                    ] })] }), _jsxs("div", { className: styles.briefField, children: [_jsx("div", { className: styles.briefLabel, children: language === "ru" ? "Язык" : "Language" }), _jsx("div", { className: styles.chipRow, children: [
                                                                        {
                                                                            value: "ru",
                                                                            label: language === "ru"
                                                                                ? "🇷🇺 Русский"
                                                                                : "🇷🇺 Russian",
                                                                        },
                                                                        {
                                                                            value: "en",
                                                                            label: language === "ru"
                                                                                ? "🇬🇧 Английский"
                                                                                : "🇬🇧 English",
                                                                        },
                                                                    ].map((opt) => (_jsx(IconChip, { selected: selectedLanguage === opt.value, onClick: () => setSelectedLanguage(opt.value), children: opt.label }, opt.value))) })] })] }), _jsx("div", { className: styles.aiPreview, children: !showAiGeneratedResult ? (_jsxs("div", { className: styles.previewEmpty, children: [_jsx("div", { className: styles.previewEmptyIcon, children: _jsx(IconSparkles, { size: 28 }) }), _jsx("div", { className: styles.previewEmptyTitle, children: language === "ru"
                                                                    ? "Текст песни появится здесь"
                                                                    : "The lyrics will appear here" }), _jsx("div", { className: styles.previewEmptyHint, children: language === "ru"
                                                                    ? "Заполните параметры слева и нажмите «Сгенерировать текст»."
                                                                    : "Fill in the parameters on the left and press “Generate text”." })] })) : (_jsxs("div", { "data-generated-result": "", className: styles.aiResult, children: [_jsx(VpTextField, { label: language === "ru"
                                                                    ? "Название песни"
                                                                    : "Song title", value: generatedTitle || createdSong?.title || "", onChange: (e) => setGeneratedTitle(e.currentTarget.value), placeholder: language === "ru"
                                                                    ? "Введите название песни"
                                                                    : "Enter song title" }), _jsx(VpTextarea, { label: language === "ru" ? "Текст песни" : "Song text", value: generatedText || createdSong?.text || "", onChange: (e) => setGeneratedText(e.currentTarget.value), placeholder: language === "ru"
                                                                    ? "Введите текст песни"
                                                                    : "Enter song text", rows: 16, style: { whiteSpace: "pre-wrap" } })] })) }), _jsxs("div", { className: styles.aiActionRow, children: [_jsx("div", { className: styles.stickyAction, children: _jsx("div", { className: styles.aiActionLeft, children: !showAiGeneratedResult ? (_jsx(VpButton, { variant: "primary", size: "lg", fullWidth: true, onClick: handleGenerateText, disabled: (!selectedReason && !customReason.trim()) ||
                                                                        !about.trim() ||
                                                                        isGeneratingText, leftSection: _jsx(IconWand, { size: 16 }), children: language === "ru"
                                                                        ? isGeneratingText
                                                                            ? "Генерируется…"
                                                                            : "Сгенерировать текст"
                                                                        : isGeneratingText
                                                                            ? "Generating…"
                                                                            : "Generate text" })) : (_jsxs(_Fragment, { children: [_jsx(VpButton, { variant: "primary", size: "lg", fullWidth: true, onClick: handleContinueWithText, disabled: isGeneratingText, children: language === "ru"
                                                                                ? "К музыке"
                                                                                : "To the music" }), _jsx(VpButton, { variant: "outline", size: "lg", fullWidth: true, onClick: handleGenerateText, disabled: isGeneratingText, leftSection: _jsx(IconRefresh, { size: 16 }), children: language === "ru"
                                                                                ? "Пересоздать текст"
                                                                                : "Regenerate text" })] })) }) }), _jsx("div", { className: styles.aiActionRight, children: showAiGeneratedResult && (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.aiEditHint, children: [_jsx(IconWand, { size: 14 }), _jsx("span", { children: language === "ru"
                                                                                    ? "Перепишет только нужное"
                                                                                    : "Rewrites only what's needed" })] }), _jsx(VpTextarea, { value: editPrompt, onChange: (e) => setEditPrompt(e.currentTarget.value), placeholder: language === "ru"
                                                                            ? "Например: «Сделай припев теплее»"
                                                                            : 'e.g., "Make the chorus warmer"', rows: 3 }), createdSong && (_jsx(VpButton, { variant: "outline", size: "md", fullWidth: true, leftSection: _jsx(IconWand, { size: 16 }), disabled: !editPrompt.trim() || isEditingWithAI, onClick: async () => {
                                                                            if (!createdSong || !createdSong.id) {
                                                                                notifications.show({
                                                                                    title: language === "ru"
                                                                                        ? "Ошибка"
                                                                                        : "Error",
                                                                                    message: language === "ru"
                                                                                        ? "Песня не создана"
                                                                                        : "Song not created",
                                                                                    color: "red",
                                                                                });
                                                                                return;
                                                                            }
                                                                            if (!editPrompt.trim()) {
                                                                                notifications.show({
                                                                                    title: language === "ru"
                                                                                        ? "Ошибка"
                                                                                        : "Error",
                                                                                    message: language === "ru"
                                                                                        ? "Введите промпт для редактирования"
                                                                                        : "Enter edit prompt",
                                                                                    color: "red",
                                                                                });
                                                                                return;
                                                                            }
                                                                            setIsEditingWithAI(true);
                                                                            try {
                                                                                // Если text_id отсутствует, используем id (для обратной совместимости)
                                                                                const textId = createdSong.text_id ||
                                                                                    createdSong.id;
                                                                                if (!textId) {
                                                                                    notifications.show({
                                                                                        title: language === "ru"
                                                                                            ? "Ошибка"
                                                                                            : "Error",
                                                                                        message: language === "ru"
                                                                                            ? "ID текста не найден"
                                                                                            : "Text ID not found",
                                                                                        color: "red",
                                                                                    });
                                                                                    return;
                                                                                }
                                                                                trackEvent(AnalyticsEvent.TEXT_EDIT_START, { text_id: textId });
                                                                                const { ws_event_id } = await editSongWithAI(textId, {
                                                                                    prompt: editPrompt,
                                                                                });
                                                                                // Ждем результат через WebSocket
                                                                                const updatedSong = await waitForSongGeneration(ws_event_id);
                                                                                setGeneratedText(updatedSong.text || "");
                                                                                setGeneratedTitle(updatedSong.title || "");
                                                                                setCreatedSong(updatedSong);
                                                                                setEditPrompt("");
                                                                                setHasCompletedAiTextGeneration(true);
                                                                                notifications.show({
                                                                                    title: language === "ru"
                                                                                        ? "Успешно"
                                                                                        : "Success",
                                                                                    message: language === "ru"
                                                                                        ? "Текст песни отредактирован"
                                                                                        : "Song text edited",
                                                                                    color: "green",
                                                                                });
                                                                            }
                                                                            catch (error) {
                                                                                console.error("Error editing song with AI:", error);
                                                                                const errorMessage = resolveTextActionErrorMessage(error, language, "Ошибка при редактировании текста", "Error editing text");
                                                                                notifications.show({
                                                                                    title: language === "ru"
                                                                                        ? "Ошибка"
                                                                                        : "Error",
                                                                                    message: errorMessage,
                                                                                    color: "red",
                                                                                });
                                                                            }
                                                                            finally {
                                                                                setIsEditingWithAI(false);
                                                                            }
                                                                        }, children: isEditingWithAI
                                                                            ? language === "ru"
                                                                                ? "Применяется…"
                                                                                : "Applying…"
                                                                            : language === "ru"
                                                                                ? "Применить"
                                                                                : "Apply" }))] })) })] })] })) })), textSourceType === "new" && textType === "custom" && (_jsxs("div", { className: styles.singleCol, children: [sourceSegment, _jsx(VpTextField, { label: language === "ru" ? "Название песни" : "Song title", placeholder: language === "ru"
                                                    ? "Введите название песни"
                                                    : "Enter song title", value: customTitle, onChange: (e) => setCustomTitle(e.currentTarget.value) }), _jsxs("div", { className: styles.generatedContainer, children: [_jsx("div", { className: styles.generatedLeft, children: _jsx(VpTextarea, { label: language === "ru" ? "Текст песни" : "Song text", placeholder: language === "ru"
                                                                ? "Напишите текст — Виртуоз превратит его в песню."
                                                                : "Write your lyrics — Virtuoso will turn them into a song.", value: customText, onChange: (e) => setCustomText(e.currentTarget.value), rows: 16 }) }), _jsxs("div", { className: styles.generatedRight, children: [_jsx(VpTextarea, { label: language === "ru"
                                                                    ? "Промпт для редактирования песни"
                                                                    : "Song editing prompt", hint: language === "ru"
                                                                    ? "Что изменить — Виртуоз перепишет нужные строки."
                                                                    : "Describe what to change — only the affected lines get rewritten.", value: editPrompt, onChange: (e) => setEditPrompt(e.currentTarget.value), placeholder: language === "ru"
                                                                    ? "Например: «Сделай припев короче»"
                                                                    : 'e.g., "Make the chorus shorter"', rows: 6 }), _jsx(VpButton, { variant: "outline", size: "md", fullWidth: true, leftSection: _jsx(IconWand, { size: 16 }), disabled: !customText.trim() ||
                                                                    isEditingWithAI ||
                                                                    !editPrompt.trim(), onClick: async () => {
                                                                    if (!customText.trim()) {
                                                                        return;
                                                                    }
                                                                    if (!editPrompt.trim()) {
                                                                        notifications.show({
                                                                            title: language === "ru" ? "Ошибка" : "Error",
                                                                            message: language === "ru"
                                                                                ? "Введите промпт для редактирования"
                                                                                : "Enter edit prompt",
                                                                            color: "red",
                                                                        });
                                                                        return;
                                                                    }
                                                                    const runCustomTextEditWithAI = async () => {
                                                                        try {
                                                                            setIsEditingWithAI(true);
                                                                            const createResponse = await createSongFromCustomText({
                                                                                title: customTitle || "",
                                                                                text: customText,
                                                                                recipient: null,
                                                                                reason: null,
                                                                            });
                                                                            const created = await waitForSongGeneration(createResponse.ws_event_id);
                                                                            const textId = created.text_id || created.id;
                                                                            trackEvent(AnalyticsEvent.TEXT_EDIT_START, { text_id: textId });
                                                                            const { ws_event_id } = await editSongWithAI(textId, {
                                                                                prompt: editPrompt,
                                                                            });
                                                                            const updated = await waitForSongGeneration(ws_event_id);
                                                                            setCustomText(updated.text || "");
                                                                            setCustomTitle(updated.title || customTitle);
                                                                            setEditPrompt("");
                                                                        }
                                                                        catch (e) {
                                                                            console.error("Error editing custom text with AI:", e);
                                                                            if (isGuestLlmLimitError(e)) {
                                                                                notifications.show({
                                                                                    title: language === "ru"
                                                                                        ? "Лимит исчерпан"
                                                                                        : "Limit reached",
                                                                                    message: resolveTextActionErrorMessage(e, language, "Ошибка при редактировании текста", "Error editing text"),
                                                                                    color: "red",
                                                                                });
                                                                            }
                                                                        }
                                                                        finally {
                                                                            setIsEditingWithAI(false);
                                                                        }
                                                                    };
                                                                    // Гостевая сессия даёт доступ к edit-with-AI, поэтому
                                                                    // авторизацию здесь не требуем — регистрация только на
                                                                    // шаге «Создать песню».
                                                                    await runCustomTextEditWithAI();
                                                                }, children: isEditingWithAI
                                                                    ? language === "ru"
                                                                        ? "Редактируется…"
                                                                        : "Editing…"
                                                                    : language === "ru"
                                                                        ? "Изменить песню"
                                                                        : "Edit song" })] })] }), _jsx("div", { className: styles.stickyAction, children: _jsx(VpButton, { variant: "primary", size: "lg", fullWidth: true, onClick: handleContinueWithText, disabled: !customText.trim() || isCreatingSong, leftSection: _jsx(IconWand, { size: 16 }), children: isCreatingSong
                                                        ? language === "ru"
                                                            ? "Создаём…"
                                                            : "Creating…"
                                                        : language === "ru"
                                                            ? "Создать хит"
                                                            : "Create a hit" }) })] })), textSourceType === "saved" && (_jsxs("div", { className: styles.singleCol, children: [sourceSegment, canUseTexts && selectedSavedText && (_jsx(VpButton, { variant: "ghost", size: "sm", className: styles.backToList, onClick: () => {
                                                    setSelectedSavedText(null);
                                                    setSavedTextEditPrompt("");
                                                    setSavedTextCreatedSong(null);
                                                }, children: language === "ru" ? "← К списку" : "← Back" })), !canUseTexts ? (_jsxs("div", { className: styles.savedEmpty, children: [_jsx("div", { className: styles.savedEmptyTitle, children: language === "ru"
                                                            ? "Войдите, чтобы увидеть свои тексты"
                                                            : "Sign in to see your texts" }), _jsx("div", { className: styles.savedEmptyHint, children: language === "ru"
                                                            ? "Все сгенерированные тексты сохраняются автоматически и доступны здесь."
                                                            : "All generated lyrics are saved automatically and listed here." }), _jsx(VpButton, { variant: "primary", size: "md", onClick: () => {
                                                            if (isDesktopAuth) {
                                                                setIsRegisterOpen(true);
                                                            }
                                                            else {
                                                                openRegisterModal();
                                                            }
                                                        }, children: language === "ru" ? "Войти" : "Sign in" })] })) : (_jsxs(_Fragment, { children: [!selectedSavedText && (_jsx(VpTextField, { placeholder: language === "ru"
                                                            ? "Поиск по текстам…"
                                                            : "Search texts…", value: savedTextsSearchQuery, onChange: (e) => setSavedTextsSearchQuery(e.target.value) })), isLoadingSavedTexts ? (_jsx("div", { style: {
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            padding: "32px 0",
                                                        }, children: _jsx(VpLoader, { size: "lg" }) })) : selectedSavedText ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 16 }, children: [_jsxs("div", { className: styles.generatedContainer, children: [_jsx("div", { className: styles.generatedLeft, children: _jsx(VpTextarea, { label: language === "ru" ? "Текст песни" : "Song text", value: selectedSavedText.text ?? "", onChange: (e) => setSelectedSavedText({
                                                                                ...selectedSavedText,
                                                                                text: e.currentTarget.value,
                                                                            }), placeholder: language === "ru"
                                                                                ? "Введите текст песни"
                                                                                : "Enter song text", rows: 16, style: { whiteSpace: "pre-wrap" } }) }), _jsxs("div", { className: styles.generatedRight, children: [_jsx(VpTextField, { label: language === "ru"
                                                                                    ? "Название песни"
                                                                                    : "Song title", value: selectedSavedText.title || "", onChange: (e) => {
                                                                                    setSelectedSavedText({
                                                                                        ...selectedSavedText,
                                                                                        title: e.currentTarget.value,
                                                                                    });
                                                                                }, placeholder: language === "ru"
                                                                                    ? "Введите название"
                                                                                    : "Enter title" }), (tagLabel(selectedSavedText.recipient) ||
                                                                                tagLabel(selectedSavedText.reason) ||
                                                                                tagLabel(selectedSavedText.genre) ||
                                                                                selectedSavedText.language) && (_jsxs("div", { className: styles.briefField, children: [_jsx("div", { className: styles.briefLabel, children: language === "ru" ? "Теги" : "Tags" }), _jsxs("div", { className: styles.chipRow, children: [tagLabel(selectedSavedText.recipient) && (_jsx("span", { className: styles.tagChip, children: tagLabel(selectedSavedText.recipient) })), tagLabel(selectedSavedText.reason) && (_jsx("span", { className: styles.tagChip, children: tagLabel(selectedSavedText.reason) })), tagLabel(selectedSavedText.genre) && (_jsx("span", { className: styles.tagChip, children: tagLabel(selectedSavedText.genre) })), selectedSavedText.language && (_jsx("span", { className: styles.tagChip, children: selectedSavedText.language === "ru"
                                                                                                    ? "Русский"
                                                                                                    : "English" }))] })] })), _jsx(VpTextarea, { label: language === "ru"
                                                                                    ? "Поправить через ИИ"
                                                                                    : "Edit with AI", hint: language === "ru"
                                                                                    ? "Что изменить — Виртуоз перепишет нужные строки."
                                                                                    : "Describe what to change — only affected lines get rewritten.", value: savedTextEditPrompt, onChange: (e) => setSavedTextEditPrompt(e.currentTarget.value), placeholder: language === "ru"
                                                                                    ? "Например: «Сделай припев теплее»"
                                                                                    : 'e.g., "Make the chorus warmer"', rows: 6 }), _jsx(VpButton, { variant: "outline", size: "md", fullWidth: true, leftSection: _jsx(IconWand, { size: 16 }), disabled: !savedTextEditPrompt.trim() ||
                                                                                    isEditingSavedTextWithAI, onClick: async () => {
                                                                                    if (!selectedSavedText) {
                                                                                        notifications.show({
                                                                                            title: language === "ru" ? "Ошибка" : "Error",
                                                                                            message: language === "ru"
                                                                                                ? "Текст не выбран"
                                                                                                : "Text not selected",
                                                                                            color: "red",
                                                                                        });
                                                                                        return;
                                                                                    }
                                                                                    if (!savedTextEditPrompt.trim()) {
                                                                                        notifications.show({
                                                                                            title: language === "ru" ? "Ошибка" : "Error",
                                                                                            message: language === "ru"
                                                                                                ? "Введите промпт для редактирования"
                                                                                                : "Enter edit prompt",
                                                                                            color: "red",
                                                                                        });
                                                                                        return;
                                                                                    }
                                                                                    setIsEditingSavedTextWithAI(true);
                                                                                    try {
                                                                                        let textToEdit = selectedSavedText;
                                                                                        if (!textToEdit || !textToEdit.id) {
                                                                                            const createResponse = await createSongFromCustomText({
                                                                                                title: selectedSavedText.title || "",
                                                                                                text: selectedSavedText.text || "",
                                                                                                recipient: typeof selectedSavedText.recipient ===
                                                                                                    "string"
                                                                                                    ? selectedSavedText.recipient
                                                                                                    : selectedSavedText.recipient
                                                                                                        ?.title || null,
                                                                                                reason: typeof selectedSavedText.reason ===
                                                                                                    "string"
                                                                                                    ? selectedSavedText.reason
                                                                                                    : selectedSavedText.reason
                                                                                                        ?.title || null,
                                                                                            });
                                                                                            const createdText = await waitForSongGeneration(createResponse.ws_event_id);
                                                                                            textToEdit = {
                                                                                                ...selectedSavedText,
                                                                                                id: createdText.text_id || createdText.id,
                                                                                                text: createdText.text,
                                                                                                title: createdText.title,
                                                                                            };
                                                                                            setSavedTextCreatedSong(createdText);
                                                                                        }
                                                                                        if (!textToEdit || !textToEdit.id) {
                                                                                            throw new Error("Failed to create text");
                                                                                        }
                                                                                        trackEvent(AnalyticsEvent.TEXT_EDIT_START, { text_id: textToEdit.id });
                                                                                        const { ws_event_id } = await editSongWithAI(textToEdit.id, {
                                                                                            prompt: savedTextEditPrompt,
                                                                                        });
                                                                                        const updatedSong = await waitForSongGeneration(ws_event_id);
                                                                                        setSelectedSavedText({
                                                                                            ...selectedSavedText,
                                                                                            title: updatedSong.title ||
                                                                                                selectedSavedText.title,
                                                                                            text: updatedSong.text ||
                                                                                                selectedSavedText.text,
                                                                                        });
                                                                                        setSavedTextCreatedSong(updatedSong);
                                                                                        setSavedTextEditPrompt("");
                                                                                        notifications.show({
                                                                                            title: language === "ru"
                                                                                                ? "Успешно"
                                                                                                : "Success",
                                                                                            message: language === "ru"
                                                                                                ? "Текст песни отредактирован"
                                                                                                : "Song text edited",
                                                                                            color: "green",
                                                                                        });
                                                                                    }
                                                                                    catch (error) {
                                                                                        console.error("Error editing saved text with AI:", error);
                                                                                        const errorMessage = resolveTextActionErrorMessage(error, language, "Ошибка при редактировании текста", "Error editing text");
                                                                                        notifications.show({
                                                                                            title: language === "ru" ? "Ошибка" : "Error",
                                                                                            message: errorMessage,
                                                                                            color: "red",
                                                                                        });
                                                                                    }
                                                                                    finally {
                                                                                        setIsEditingSavedTextWithAI(false);
                                                                                    }
                                                                                }, children: isEditingSavedTextWithAI
                                                                                    ? language === "ru"
                                                                                        ? "Редактируется…"
                                                                                        : "Editing…"
                                                                                    : language === "ru"
                                                                                        ? "Изменить песню"
                                                                                        : "Edit song" })] })] }), _jsx(VpButton, { variant: "primary", size: "lg", fullWidth: true, disabled: !selectedSavedText?.text?.trim() || isCreatingSong, onClick: async () => {
                                                                    if (!selectedSavedText?.text?.trim()) {
                                                                        return;
                                                                    }
                                                                    if (!selectedSavedText || !selectedSavedText.id) {
                                                                        notifications.show({
                                                                            title: language === "ru" ? "Ошибка" : "Error",
                                                                            message: language === "ru"
                                                                                ? "Текст не выбран"
                                                                                : "Text not selected",
                                                                            color: "red",
                                                                        });
                                                                        return;
                                                                    }
                                                                    const runSavedTextContinue = async () => {
                                                                        setIsCreatingSong(true);
                                                                        try {
                                                                            const updatedSong = await editSongDirect(selectedSavedText.id, {
                                                                                text: selectedSavedText.text || "",
                                                                                title: selectedSavedText.title?.trim() ||
                                                                                    undefined,
                                                                            });
                                                                            const song = {
                                                                                id: updatedSong.id,
                                                                                title: updatedSong.title || "",
                                                                                text: updatedSong.text || "",
                                                                                status: updatedSong.status,
                                                                                text_id: updatedSong.text_id || updatedSong.id,
                                                                                recipient: updatedSong.recipient,
                                                                                reason: updatedSong.reason,
                                                                                genre: updatedSong.genre,
                                                                                about: updatedSong.about,
                                                                                audios: updatedSong.audios || [],
                                                                                created_at: updatedSong.created_at,
                                                                            };
                                                                            setCreatedSong(song);
                                                                            setTextType("custom");
                                                                            setTextSourceType("new");
                                                                            setIsTextStepCompleted(true);
                                                                            setActiveTab("melody");
                                                                        }
                                                                        catch (error) {
                                                                            console.error("Error updating saved text:", error);
                                                                            const errorMessage = error.response?.data?.detail ||
                                                                                error.response?.data?.message ||
                                                                                error.message ||
                                                                                (language === "ru"
                                                                                    ? "Ошибка при обновлении текста"
                                                                                    : "Error updating text");
                                                                            notifications.show({
                                                                                title: language === "ru" ? "Ошибка" : "Error",
                                                                                message: errorMessage,
                                                                                color: "red",
                                                                            });
                                                                        }
                                                                        finally {
                                                                            setIsCreatingSong(false);
                                                                        }
                                                                    };
                                                                    // Гостевая сессия даёт доступ к ручкам текстов,
                                                                    // поэтому авторизацию здесь не требуем — гость
                                                                    // переходит к настройкам песни. Регистрация
                                                                    // спрашивается только на «Создать песню».
                                                                    await runSavedTextContinue();
                                                                }, children: isCreatingSong
                                                                    ? language === "ru"
                                                                        ? "Сохраняем…"
                                                                        : "Saving…"
                                                                    : language === "ru"
                                                                        ? "Продолжить с этим текстом →"
                                                                        : "Continue with this text →" })] })) : (_jsx("div", { className: styles.savedList, children: savedTexts.length === 0 ? (_jsx("div", { className: styles.savedEmpty, children: _jsx("div", { className: styles.savedEmptyHint, children: language === "ru"
                                                                    ? savedTextsSearchQuery.trim()
                                                                        ? "Тексты не найдены"
                                                                        : "Сохранённых текстов пока нет"
                                                                    : savedTextsSearchQuery.trim()
                                                                        ? "No texts found"
                                                                        : "No saved texts yet" }) })) : (savedTexts.map((text) => (_jsxs("button", { type: "button", className: styles.savedCard, onClick: () => {
                                                                setSelectedSavedText(text);
                                                                setSavedTextEditPrompt("");
                                                                setSavedTextCreatedSong(null);
                                                                trackEvent(AnalyticsEvent.TEXT_SELECT, {
                                                                    text_id: text.id,
                                                                    source: "history",
                                                                });
                                                                // Восстанавливаем настройки текста
                                                                if (text.recipient &&
                                                                    typeof text.recipient === "object" &&
                                                                    text.recipient !== null &&
                                                                    "id" in text.recipient) {
                                                                    const recipientObj = text.recipient;
                                                                    const recipient = recipients.find((r) => r.id === recipientObj.id);
                                                                    if (recipient)
                                                                        setSelectedRecipient(recipient);
                                                                }
                                                                if (text.reason &&
                                                                    typeof text.reason === "object" &&
                                                                    text.reason !== null &&
                                                                    "id" in text.reason) {
                                                                    const reasonObj = text.reason;
                                                                    const reason = reasons.find((r) => r.id === reasonObj.id);
                                                                    if (reason)
                                                                        setSelectedReason(reason);
                                                                }
                                                                if (text.genre &&
                                                                    typeof text.genre === "object" &&
                                                                    text.genre !== null &&
                                                                    "id" in text.genre) {
                                                                    const genreObj = text.genre;
                                                                    const genre = genres.find((g) => g.id === genreObj.id);
                                                                    if (genre)
                                                                        setSelectedGenre(genre);
                                                                }
                                                                if (text.language) {
                                                                    setSelectedLanguage(text.language);
                                                                }
                                                                if (text.about) {
                                                                    setAbout(text.about);
                                                                }
                                                            }, children: [_jsxs("div", { className: styles.savedCardHead, children: [_jsx("span", { className: styles.savedTitle, children: text.title ||
                                                                                (language === "ru"
                                                                                    ? "Без названия"
                                                                                    : "No title") }), _jsx("span", { className: styles.savedDate, children: new Date(text.created_at).toLocaleDateString(language === "ru" ? "ru-RU" : "en-US") })] }), _jsx("p", { className: styles.savedPreview, children: text.text }), (tagLabel(text.recipient) ||
                                                                    tagLabel(text.reason) ||
                                                                    tagLabel(text.genre) ||
                                                                    text.language) && (_jsxs("div", { className: styles.savedCardTags, children: [tagLabel(text.recipient) && (_jsx("span", { className: styles.tagChip, children: tagLabel(text.recipient) })), tagLabel(text.reason) && (_jsx("span", { className: styles.tagChip, children: tagLabel(text.reason) })), tagLabel(text.genre) && (_jsx("span", { className: styles.tagChip, children: tagLabel(text.genre) })), text.language && (_jsx("span", { className: styles.tagChip, children: text.language === "ru"
                                                                                ? "Русский"
                                                                                : "English" }))] }))] }, text.id)))) }))] }))] }))] }));
                        })() })), activeTab === "melody" && (_jsxs("div", { className: cn(styles.singleCol, styles.melodyCol), children: [_jsx("div", { className: styles.melodyBack, children: _jsx(VpButton, { variant: "ghost", size: "sm", leftSection: _jsx(IconChevronLeft, { size: 16, stroke: 2 }), onClick: () => {
                                        setActiveTab("text");
                                        requestAnimationFrame(() => scrollGenerateSongPageToTop());
                                    }, children: language === "ru"
                                        ? "Вернуться к тексту"
                                        : "Back to lyrics" }) }), _jsxs("div", { className: styles.briefField, children: [_jsx("div", { className: styles.briefLabel, children: language === "ru" ? "Голос" : "Voice" }), _jsxs("div", { className: styles.chipRow, children: [_jsx(IconChip, { selected: selectedVoice === "m" && selectedVoiceProfileId == null, onClick: () => {
                                                    setSelectedVoice("m");
                                                    setSelectedVoiceProfileId(null);
                                                    trackEvent(AnalyticsEvent.VOICE_SELECT, {
                                                        value: "male",
                                                        text_id: createdSong?.text_id ?? createdSong?.id,
                                                    });
                                                }, children: language === "ru" ? "👨 Мужской" : "👨 Male" }), _jsx(IconChip, { selected: selectedVoice === "f" && selectedVoiceProfileId == null, onClick: () => {
                                                    setSelectedVoice("f");
                                                    setSelectedVoiceProfileId(null);
                                                    trackEvent(AnalyticsEvent.VOICE_SELECT, {
                                                        value: "female",
                                                        text_id: createdSong?.text_id ?? createdSong?.id,
                                                    });
                                                }, children: language === "ru" ? "👩 Женский" : "👩 Female" }), _jsx(IconChip, { selected: selectedVoice === "d" && selectedVoiceProfileId == null, onClick: () => {
                                                    setSelectedVoice("d");
                                                    setSelectedVoiceProfileId(null);
                                                    trackEvent(AnalyticsEvent.VOICE_SELECT, {
                                                        value: "duet",
                                                        text_id: createdSong?.text_id ?? createdSong?.id,
                                                    });
                                                }, children: language === "ru" ? "👫 Дуэт" : "👫 Duet" }), _jsx(IconChip, { selected: selectedVoice === "c" && selectedVoiceProfileId == null, onClick: () => {
                                                    setSelectedVoice("c");
                                                    setSelectedVoiceProfileId(null);
                                                    trackEvent(AnalyticsEvent.VOICE_SELECT, {
                                                        value: "children",
                                                        text_id: createdSong?.text_id ?? createdSong?.id,
                                                    });
                                                }, children: language === "ru" ? "🧒 Детский" : "🧒 Kids" }), _jsx(IconChip, { selected: selectedVoice === "h" && selectedVoiceProfileId == null, onClick: () => {
                                                    setSelectedVoice("h");
                                                    setSelectedVoiceProfileId(null);
                                                    trackEvent(AnalyticsEvent.VOICE_SELECT, {
                                                        value: "choir",
                                                        text_id: createdSong?.text_id ?? createdSong?.id,
                                                    });
                                                }, children: language === "ru" ? "🎼 Хор" : "🎼 Choir" })] }), voiceProfiles.length > 0 && (_jsx("div", { className: styles.chipRow, style: { marginTop: 12 }, children: voiceProfiles.map((profile) => {
                                            const ready = isVoiceProfileReady(profile);
                                            const statusText = ready
                                                ? ""
                                                : String(profile.status).toLowerCase() === "failed"
                                                    ? language === "ru"
                                                        ? " (ошибка)"
                                                        : " (failed)"
                                                    : String(profile.status).toLowerCase() === "expired"
                                                        ? language === "ru"
                                                            ? " (устарел)"
                                                            : " (expired)"
                                                        : language === "ru"
                                                            ? " (готовится…)"
                                                            : " (processing…)";
                                            return (_jsx(IconChip, { selected: selectedVoiceProfileId === profile.id, disabled: !ready, onClick: () => {
                                                    if (!ready)
                                                        return;
                                                    // Тумблер: повторный клик снимает выбор голоса.
                                                    setSelectedVoiceProfileId((prev) => prev === profile.id ? null : profile.id);
                                                }, children: `🎤 ${profile.name ||
                                                    (language === "ru" ? "Мой голос" : "My voice")}${statusText}` }, profile.id));
                                        }) }))] }), _jsxs("div", { className: styles.briefField, children: [_jsx("div", { className: styles.briefLabel, children: language === "ru" ? "Жанр" : "Genre" }), _jsx("div", { className: styles.chipRow, children: genres.map((genre) => (_jsx(IconChip, { selected: selectedGenre?.id === genre.id, onClick: () => {
                                                setSelectedGenre(genre);
                                                trackEvent(AnalyticsEvent.GENRE_SELECT, {
                                                    value: genre.title,
                                                    text_id: createdSong?.text_id ?? createdSong?.id,
                                                });
                                            }, children: genre.title }, genre.id))) })] }), _jsxs("div", { className: styles.briefField, children: [_jsxs("div", { className: styles.briefLabel, children: [language === "ru" ? "Настроение" : "Mood", _jsx("span", { className: styles.briefLabelSuffix, children: language === "ru" ? " (несколько)" : " (multiple)" })] }), _jsx("div", { className: styles.chipRow, children: moods.map((mood) => {
                                            const isSelected = selectedMoods.some((m) => m.id === mood.id);
                                            return (_jsx(IconChip, { selected: isSelected, onClick: () => {
                                                    if (isSelected) {
                                                        setSelectedMoods(selectedMoods.filter((m) => m.id !== mood.id));
                                                        trackEvent(AnalyticsEvent.MOOD_DISELECT, {
                                                            value: mood.title,
                                                            text_id: createdSong?.text_id ?? createdSong?.id,
                                                        });
                                                    }
                                                    else {
                                                        setSelectedMoods([...selectedMoods, mood]);
                                                        trackEvent(AnalyticsEvent.MOOD_SELECT, {
                                                            value: mood.title,
                                                            text_id: createdSong?.text_id ?? createdSong?.id,
                                                        });
                                                    }
                                                }, children: stripLeadEmoji(mood.title) }, mood.id));
                                        }) })] }), _jsx(StyleReferenceField, { language: language, value: styleReference, onChange: setStyleReference, onBusyChange: setIsAnalyzingReference }), _jsx(Accordion, { items: [
                                    {
                                        id: "extra",
                                        title: (_jsxs("span", { className: styles.melodyExtraTitle, children: [_jsx("span", { className: styles.melodyExtraBadge, children: "+" }), _jsxs("span", { className: styles.melodyExtraText, children: [_jsx("span", { className: styles.melodyExtraName, children: language === "ru"
                                                                ? "Доп. параметры"
                                                                : "Extra parameters" }), _jsx("span", { className: styles.melodyExtraSub, children: language === "ru"
                                                                ? "Инструменты, тональность, описание"
                                                                : "Instruments, key, description" })] })] })),
                                        content: (_jsxs("div", { className: styles.melodyExtraContent, children: [_jsxs("div", { className: styles.briefField, children: [_jsx("div", { className: styles.briefLabel, children: language === "ru"
                                                                ? "Инструменты · можно несколько"
                                                                : "Instruments · multiple" }), _jsx("div", { className: styles.chipRow, children: instruments.map((instrument) => {
                                                                const isSelected = selectedInstruments.some((i) => i.id === instrument.id);
                                                                return (_jsx(IconChip, { selected: isSelected, onClick: () => {
                                                                        if (isSelected) {
                                                                            setSelectedInstruments(selectedInstruments.filter((i) => i.id !== instrument.id));
                                                                            trackEvent(AnalyticsEvent.INSTRUMENT_DISELECT, {
                                                                                value: instrument.title,
                                                                                text_id: createdSong?.text_id ??
                                                                                    createdSong?.id,
                                                                            });
                                                                        }
                                                                        else {
                                                                            setSelectedInstruments([
                                                                                ...selectedInstruments,
                                                                                instrument,
                                                                            ]);
                                                                            trackEvent(AnalyticsEvent.INSTRUMENT_SELECT, {
                                                                                value: instrument.title,
                                                                                text_id: createdSong?.text_id ??
                                                                                    createdSong?.id,
                                                                            });
                                                                        }
                                                                    }, children: stripLeadEmoji(instrument.title) }, instrument.id));
                                                            }) })] }), _jsx(VpSelect, { label: language === "ru" ? "Тональность" : "Key", hint: language === "ru"
                                                        ? "Опционально. Если не выбрана — Виртуоз решит сам."
                                                        : "Optional. Left empty, Virtuoso picks.", value: selectedKey ?? "", onChange: (e) => {
                                                        const key = e.currentTarget.value || null;
                                                        setSelectedKey(key);
                                                        if (key) {
                                                            trackEvent(AnalyticsEvent.KEY_SELECT, {
                                                                value: key,
                                                                text_id: createdSong?.text_id ?? createdSong?.id,
                                                            });
                                                        }
                                                    }, placeholder: language === "ru" ? "Без тональности" : "No key", options: MELODY_KEY_OPTIONS }), _jsx(VpTextarea, { label: language === "ru"
                                                        ? "Дополнительное описание стиля"
                                                        : "Extra style description", hint: language === "ru"
                                                        ? "Любые свободные подсказки про звук, темп, инструменты."
                                                        : "Free-form notes on sound, tempo, instruments.", placeholder: language === "ru"
                                                        ? "Например: «акустика + лёгкая перкуссия, темп вальса»"
                                                        : 'e.g., "acoustic with light percussion, waltz tempo"', value: extraDescription, onChange: (e) => setExtraDescription(e.currentTarget.value), onBlur: (e) => {
                                                        const v = e.currentTarget.value.trim();
                                                        if (v) {
                                                            trackEvent(AnalyticsEvent.STYLE_EXTRA_INPUT, {
                                                                extra_len: v.length,
                                                                text_id: createdSong?.text_id ?? createdSong?.id,
                                                            });
                                                        }
                                                    }, rows: 3, maxLength: 999 })] })),
                                    },
                                ] }), _jsx("div", { className: styles.stickyAction, children: _jsx(VpButton, { variant: "primary", size: "lg", fullWidth: true, onClick: handleCreateSong, disabled: isCreatingSong || repeatOptionsPending || isAnalyzingReference, leftSection: _jsx(IconWand, { size: 16 }), children: isCreatingSong
                                        ? language === "ru"
                                            ? "Создаём песню…"
                                            : "Creating song…"
                                        : isAnalyzingReference
                                            ? language === "ru"
                                                ? "Анализируем референс…"
                                                : "Analyzing reference…"
                                            : repeatOptionsPending
                                                ? language === "ru"
                                                    ? "Загружаем настройки…"
                                                    : "Loading settings…"
                                                : language === "ru"
                                                    ? "Создать песню"
                                                    : "Create Song" }) })] }))] })] }));
};
